import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { getAllSpecialtyOptions, baseSpecialties, hasSubmodules, getSubmodules } from '../data/specialtyStructure';
import UserUsageAdmin from './UserUsageAdmin';
import UserAccessAdmin from './UserAccessAdmin';
import PlannerOrderAdmin from './PlannerOrderAdmin';
import { 
  Plus, 
  Edit, 
  Trash2, 
  Users, 
  BookOpen, 
  ArrowLeft,
  Check,
  X,
  RotateCcw,
  Upload
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const apiClient = axios.create({
  baseURL: API,
});

apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

const AdminPanel = ({ user, onLogout, onUserUpdate }) => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('questions');
  const [questions, setQuestions] = useState([]);
  const [selectedQuestions, setSelectedQuestions] = useState([]);
  const [pendingUsers, setPendingUsers] = useState([]);
  const [approvedUsers, setApprovedUsers] = useState([]);
  const [clinicalCases, setClinicalCases] = useState([]);
  const [selectedCases, setSelectedCases] = useState([]);
  const [ccBalance, setCcBalance] = useState(null);
  const [showCcConfirm, setShowCcConfirm] = useState(false);
  const [imagenDXCases, setImagenDXCases] = useState([]);
  const [selectedImagenDX, setSelectedImagenDX] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showImportForm, setShowImportForm] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [filterSpecialty, setFilterSpecialty] = useState('');
  const [filterTopic, setFilterTopic] = useState('');
  const [sortBy, setSortBy] = useState('created_at');
  const [questionsPage, setQuestionsPage] = useState(0);
  const [questionsTotal, setQuestionsTotal] = useState(0);
  const QUESTIONS_PER_PAGE = 50;
  
  // Estados para modal de confirmación de eliminación
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteType, setDeleteType] = useState(''); // 'cases' o 'imagendx'
  const [deleting, setDeleting] = useState(false);
  
  // Estado para modal de confirmación de usuario (rechazar/eliminar)
  const [userAction, setUserAction] = useState(null); // { userId, userName, type: 'reject' | 'delete' }
  
  // Estados para selector de módulo/submódulo en importación
  const [selectedModule, setSelectedModule] = useState('');
  const [selectedSubmodule, setSelectedSubmodule] = useState(''); // created_at, specialty, topic
  
  // Estados para Imagen DX
  const [selectedSystem, setSelectedSystem] = useState('');
  const [imagenDXFile, setImagenDXFile] = useState(null);
  const [imagenDXImages, setImagenDXImages] = useState({}); // {case_id: File}
  
  // Estados para Soporte
  const [supportTickets, setSupportTickets] = useState([]);
  const [supportUnreadCount, setSupportUnreadCount] = useState(0);
  const [supportReplyText, setSupportReplyText] = useState('');
  const [supportExpandedTicket, setSupportExpandedTicket] = useState(null);
  
  // Form states
  const [formData, setFormData] = useState({
    question_text: '',
    option_a: '',
    option_b: '',
    option_c: '',
    option_d: '',
    correct_answer: 'A',
    explanation: '',
    reference: '',
    specialty: 'Medicina Interna',
    topic: ''
  });

  // Obtener todas las opciones de especialidad incluyendo submódulos
  const allSpecialtyOptions = getAllSpecialtyOptions();
  
  // Mantener las especialidades base para algunos casos
  const specialties = baseSpecialties;

  useEffect(() => {
    fetchData();
  }, [activeTab, filterSpecialty, filterTopic, sortBy, questionsPage]);

  // Fetch support unread count periodically
  useEffect(() => {
    const fetchSupportUnread = async () => {
      try {
        const res = await apiClient.get('/support/unread-count');
        setSupportUnreadCount(res.data.count || 0);
      } catch (e) { /* ignore */ }
    };
    fetchSupportUnread();
    const interval = setInterval(fetchSupportUnread, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      if (activeTab === 'questions') {
        let url = '/questions';
        const params = [];
        if (filterSpecialty) params.push(`specialty=${encodeURIComponent(filterSpecialty)}`);
        if (filterTopic) params.push(`topic=${encodeURIComponent(filterTopic)}`);
        const countParams = [...params];
        params.push(`limit=${QUESTIONS_PER_PAGE}`);
        params.push(`skip=${questionsPage * QUESTIONS_PER_PAGE}`);
        url += `?${params.join('&')}`;

        const countUrl = `/questions/count${countParams.length ? `?${countParams.join('&')}` : ''}`;
        const [response, countRes] = await Promise.all([
          apiClient.get(url),
          apiClient.get(countUrl),
        ]);
        setQuestionsTotal(countRes.data.count);
        let sortedQuestions = [...response.data];
        
        // Sort questions (current page)
        sortedQuestions.sort((a, b) => {
          if (sortBy === 'specialty') return a.specialty.localeCompare(b.specialty);
          if (sortBy === 'topic') return a.topic.localeCompare(b.topic);
          if (sortBy === 'created_at') return new Date(b.created_at) - new Date(a.created_at);
          return 0;
        });
        
        setQuestions(sortedQuestions);
      } else if (activeTab === 'users') {
        const [pendingResponse, approvedResponse] = await Promise.all([
          apiClient.get('/admin/pending-users'),
          apiClient.get('/admin/approved-users')
        ]);
        setPendingUsers(pendingResponse.data);
        setApprovedUsers(approvedResponse.data);
      } else if (activeTab === 'cases') {
        const response = await apiClient.get('/cases');
        setClinicalCases(response.data);
      } else if (activeTab === 'imagendx') {
        const response = await apiClient.get('/imagendx/cases');
        setImagenDXCases(response.data);
      } else if (activeTab === 'support') {
        const response = await apiClient.get('/support/all-tickets');
        setSupportTickets(response.data);
      }
    } catch (error) {
      toast.error('Error al cargar los datos');
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const resetForm = () => {
    setFormData({
      question_text: '',
      option_a: '',
      option_b: '',
      option_c: '',
      option_d: '',
      correct_answer: 'A',
      explanation: '',
      reference: '',
      specialty: 'Medicina Interna',
      topic: ''
    });
  };

  const handleCreateQuestion = async (e) => {
    e.preventDefault();
    
    // Validate form
    const requiredFields = ['question_text', 'option_a', 'option_b', 'option_c', 'option_d', 'explanation', 'reference', 'topic'];
    const emptyFields = requiredFields.filter(field => !formData[field].trim());
    
    if (emptyFields.length > 0) {
      toast.error('Por favor complete todos los campos');
      return;
    }

    try {
      await apiClient.post('/admin/questions', formData);
      toast.success('Pregunta creada exitosamente');
      resetForm();
      setShowCreateForm(false);
      fetchData();
    } catch (error) {
      toast.error('Error al crear la pregunta');
      console.error('Error creating question:', error);
    }
  };

  const handleDeleteQuestion = async (questionId) => {
    if (!window.confirm('¿Estás seguro de que quieres eliminar esta pregunta?')) {
      return;
    }

    try {
      await apiClient.delete(`/admin/questions/${questionId}`);
      toast.success('Pregunta eliminada exitosamente');
      fetchData();
    } catch (error) {
      toast.error('Error al eliminar la pregunta');
      console.error('Error deleting question:', error);
    }
  };

  const handleSelectQuestion = (questionId) => {
    setSelectedQuestions(prev => {
      if (prev.includes(questionId)) {
        return prev.filter(id => id !== questionId);
      } else {
        return [...prev, questionId];
      }
    });
  };

  const handleSelectAllQuestions = () => {
    if (selectedQuestions.length === questions.length) {
      setSelectedQuestions([]);
    } else {
      setSelectedQuestions(questions.map(q => q.id));
    }
  };

  const handleDeleteSelected = async () => {
    if (selectedQuestions.length === 0) {
      toast.error('No hay preguntas seleccionadas');
      return;
    }

    if (window.confirm(`¿Estás seguro de eliminar ${selectedQuestions.length} pregunta${selectedQuestions.length > 1 ? 's' : ''}?`)) {
      try {
        const response = await apiClient.post('/admin/questions/delete-multiple', selectedQuestions);
        toast.success(`${response.data.deleted_count} pregunta${response.data.deleted_count > 1 ? 's' : ''} eliminada${response.data.deleted_count > 1 ? 's' : ''} exitosamente`);
        setSelectedQuestions([]);
        fetchData();
      } catch (error) {
        toast.error('Error al eliminar las preguntas');
      }
    }
  };

  // Handlers para selección múltiple de Casos Clínicos
  const handleSelectCase = (caseId) => {
    setSelectedCases(prev => {
      if (prev.includes(caseId)) {
        return prev.filter(id => id !== caseId);
      } else {
        return [...prev, caseId];
      }
    });
  };

  const handleSelectAllCases = () => {
    if (selectedCases.length === clinicalCases.length) {
      setSelectedCases([]);
    } else {
      setSelectedCases(clinicalCases.map(c => c.id));
    }
  };

  const openDeleteCasesModal = () => {
    if (selectedCases.length === 0) {
      toast.error('No hay casos clínicos seleccionados');
      return;
    }
    setDeleteType('cases');
    setShowDeleteModal(true);
  };

  const handleDeleteSelectedCases = async () => {
    setDeleting(true);
    try {
      let deletedCount = 0;
      let errors = [];
      
      for (const caseId of selectedCases) {
        try {
          await apiClient.delete(`/admin/cases/${caseId}`);
          deletedCount++;
        } catch (err) {
          errors.push(caseId);
          console.error(`Error deleting case ${caseId}:`, err);
        }
      }
      
      if (deletedCount > 0) {
        toast.success(`${deletedCount} caso${deletedCount > 1 ? 's' : ''} clínico${deletedCount > 1 ? 's' : ''} eliminado${deletedCount > 1 ? 's' : ''} exitosamente`);
      }
      
      if (errors.length > 0) {
        toast.error(`Error al eliminar ${errors.length} caso${errors.length > 1 ? 's' : ''}`);
      }
      
      setSelectedCases([]);
      setShowDeleteModal(false);
      await fetchData();
    } catch (error) {
      console.error('Error in handleDeleteSelectedCases:', error);
      toast.error('Error al eliminar los casos clínicos');
    } finally {
      setDeleting(false);
    }
  };

  // Balanceo de opciones (IA) para Casos Clínicos seleccionados
  const fetchCcBalanceStatus = async () => {
    try {
      const res = await apiClient.get('/admin/cases/balance-status');
      setCcBalance(res.data);
      return res.data;
    } catch (err) {
      return null;
    }
  };

  const handleStartCcBalance = async () => {
    setShowCcConfirm(false);
    if (selectedCases.length === 0) {
      toast.error('No hay casos clínicos seleccionados');
      return;
    }
    try {
      const res = await apiClient.post('/admin/cases/balance-options', { case_ids: selectedCases });
      toast.success(res.data.message);
      fetchCcBalanceStatus();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al iniciar el balanceo');
    }
  };

  useEffect(() => {
    if (activeTab === 'cases') fetchCcBalanceStatus();
  }, [activeTab]);

  useEffect(() => {
    if (!ccBalance?.running) return;
    const interval = setInterval(async () => {
      const st = await fetchCcBalanceStatus();
      if (st && !st.running) {
        clearInterval(interval);
        setSelectedCases([]);
        await fetchData();
        if (st.status === 'completed') {
          toast.success(`Balanceo completado: ${st.questions_fixed} preguntas ajustadas en ${st.processed_cases} caso(s)`);
        } else if (st.status === 'error') {
          toast.error(`Error en balanceo: ${st.error || 'desconocido'}`);
        }
      }
    }, 3000);
    return () => clearInterval(interval);
  }, [ccBalance?.running]);


  // Handlers para selección múltiple de Imagen DX
  const handleSelectImagenDX = (caseId) => {
    setSelectedImagenDX(prev => {
      if (prev.includes(caseId)) {
        return prev.filter(id => id !== caseId);
      } else {
        return [...prev, caseId];
      }
    });
  };

  const handleSelectAllImagenDX = () => {
    if (selectedImagenDX.length === imagenDXCases.length) {
      setSelectedImagenDX([]);
    } else {
      setSelectedImagenDX(imagenDXCases.map(c => c.id));
    }
  };

  const openDeleteImagenDXModal = () => {
    if (selectedImagenDX.length === 0) {
      toast.error('No hay casos de Imagen DX seleccionados');
      return;
    }
    setDeleteType('imagendx');
    setShowDeleteModal(true);
  };

  const handleDeleteSelectedImagenDX = async () => {
    setDeleting(true);
    try {
      let deletedCount = 0;
      let errors = [];
      
      for (const caseId of selectedImagenDX) {
        try {
          await apiClient.delete(`/admin/imagendx/cases/${caseId}`);
          deletedCount++;
        } catch (err) {
          errors.push(caseId);
          console.error(`Error deleting ImagenDX case ${caseId}:`, err);
        }
      }
      
      if (deletedCount > 0) {
        toast.success(`${deletedCount} caso${deletedCount > 1 ? 's' : ''} de Imagen DX eliminado${deletedCount > 1 ? 's' : ''} exitosamente`);
      }
      
      if (errors.length > 0) {
        toast.error(`Error al eliminar ${errors.length} caso${errors.length > 1 ? 's' : ''}`);
      }
      
      setSelectedImagenDX([]);
      setShowDeleteModal(false);
      await fetchData();
    } catch (error) {
      console.error('Error in handleDeleteSelectedImagenDX:', error);
      toast.error('Error al eliminar los casos de Imagen DX');
    } finally {
      setDeleting(false);
    }
  };

  const handleConfirmDelete = () => {
    if (deleteType === 'cases') {
      handleDeleteSelectedCases();
    } else if (deleteType === 'imagendx') {
      handleDeleteSelectedImagenDX();
    }
  };

  const handleApproveUser = async (userId) => {
    try {
      await apiClient.patch(`/admin/approve-user/${userId}`);
      toast.success('Usuario aprobado exitosamente');
      fetchData();
    } catch (error) {
      toast.error('Error al aprobar el usuario');
      console.error('Error approving user:', error);
    }
  };

  const handleRejectUser = async (userId) => {
    try {
      await apiClient.delete(`/admin/reject-user/${userId}`);
      toast.success('Usuario rechazado exitosamente');
      setUserAction(null);
      fetchData();
    } catch (error) {
      toast.error('Error al rechazar el usuario');
      console.error('Error rejecting user:', error);
    }
  };

  const handleResetLeaderboard = async () => {
    if (!window.confirm('¿Estás seguro de que quieres resetear el ranking? Esta acción no se puede deshacer.')) {
      return;
    }

    try {
      await apiClient.delete('/admin/reset-leaderboard');
      toast.success('Ranking reseteado exitosamente');
    } catch (error) {
      toast.error('Error al resetear el ranking');
      console.error('Error resetting leaderboard:', error);
    }
  };

  const handleImportFile = async (e) => {
    e.preventDefault();
    
    if (!importFile) {
      toast.error('Por favor selecciona un archivo');
      return;
    }

    if (!selectedModule) {
      toast.error('Por favor selecciona un módulo');
      return;
    }

    // Validate submodule selection for modules that have submodules
    if (hasSubmodules(selectedModule) && !selectedSubmodule) {
      toast.error('Por favor selecciona un submódulo');
      return;
    }

    setImporting(true);
    
    try {
      const formData = new FormData();
      formData.append('file', importFile);

      // Build the specialty value based on selection
      let specialtyValue = selectedModule;
      if (hasSubmodules(selectedModule) && selectedSubmodule) {
        specialtyValue = `${selectedModule} - ${selectedSubmodule}`;
      }

      // Send specialty as query parameter
      const response = await apiClient.post(
        `/admin/import-questions?specialty=${encodeURIComponent(specialtyValue)}`, 
        formData, 
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      toast.success(response.data.message);
      
      if (response.data.errors && response.data.errors.length > 0) {
        toast.warning(`Se encontraron ${response.data.errors.length} errores. Revisa los datos.`);
        console.log('Import errors:', response.data.errors);
      }

      setImportFile(null);
      setShowImportForm(false);
      setSelectedModule('');
      setSelectedSubmodule('');
      fetchData(); // Refresh questions list
      
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al importar el archivo');
      console.error('Error importing file:', error);
    } finally {
      setImporting(false);
    }
  };

  const downloadTemplate = async () => {
    try {
      const response = await apiClient.get('/admin/import-template');
      
      // Create CSV template content
      const headers = response.data.template_columns.join(',');
      const example = response.data.template_columns.map(col => 
        `"${response.data.example_row[col][0]}"`
      ).join(',');
      
      const csvContent = `${headers}\n${example}`;
      
      // Create download
      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'template_preguntas_enarm.csv';
      link.click();
      window.URL.revokeObjectURL(url);
      
      toast.success('Template descargado exitosamente');
    } catch (error) {
      toast.error('Error al descargar el template');
      console.error('Error downloading template:', error);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 via-white to-cyan-50">
      {/* Navigation */}
      <nav className="nav-container">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap justify-between items-center gap-3">
            <div className="flex flex-wrap items-center gap-3 min-w-0">
              <Link
                to="/"
                data-testid="back-to-dashboard"
                className="flex items-center space-x-2 text-teal-600 hover:text-teal-800 transition-colors font-medium"
              >
                <ArrowLeft size={20} />
                <span>Menú Principal</span>
              </Link>
              <h1 data-testid="admin-panel-title" className="text-xl font-bold text-teal-800 break-words">⚙️ Panel de Administración</h1>
            </div>
            
            <div className="flex items-center gap-3 min-w-0 max-w-full">
              {user.profile_image ? (
                <img
                  src={user.profile_image}
                  alt={user.full_name}
                  className="w-8 h-8 rounded-full object-cover border border-teal-300"
                />
              ) : (
                <div className="w-8 h-8 rounded-full bg-teal-600 flex items-center justify-center text-white font-bold border border-teal-300">
                  {user.full_name.charAt(0).toUpperCase()}
                </div>
              )}
              <span data-testid="admin-panel-user-name" className="text-gray-700 font-medium min-w-0 [overflow-wrap:anywhere]">{user.full_name}</span>
              <button
                onClick={onLogout}
                data-testid="admin-panel-logout"
                className="shrink-0 text-red-600 hover:text-red-800 transition-colors"
              >
                Salir
              </button>
            </div>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Tabs - Grid layout */}
        <div className="mb-8">
          <nav className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2">
            {[
              { key: 'masterclasses', label: 'Masterclasses', icon: <BookOpen size={16} className="inline" />, testId: 'masterclasses-tab' },
              { key: 'questions', label: 'Preguntas', icon: <BookOpen size={16} className="inline" />, testId: 'questions-tab' },
              { key: 'users', label: `Usuarios (${pendingUsers.length})`, icon: <Users size={16} className="inline" />, testId: 'users-tab' },
              { key: 'cases', label: 'Casos Clinicos', icon: null, testId: 'cases-tab' },
              { key: 'imagendx', label: 'Imagen DX', icon: null, testId: 'imagendx-tab' },
              { key: 'presentations', label: 'Presentaciones', icon: null, testId: 'presentations-tab' },
              { key: 'duel-questions', label: 'Preg. Duelos', icon: null, testId: 'duel-questions-tab' },
              { key: 'simulacros', label: 'Simulacros', icon: null, testId: 'simulacros-tab' },
              { key: 'card-game', label: 'Juego Cartas', icon: null, testId: 'card-game-tab' },
              { key: 'admin-points', label: 'Puntos Admin', icon: null, testId: 'admin-points-tab' },
              { key: 'usage', label: 'Uso Usuarios', icon: null, testId: 'usage-tab' },
              { key: 'user-access', label: 'Gestión Usuarios', icon: null, testId: 'user-access-tab' },
              { key: 'planner-order', label: 'Orden Planificador', icon: null, testId: 'planner-order-tab' },
              { key: 'journal', label: 'Journal', icon: null, testId: 'journal-tab' },
              { key: 'support', label: 'Soporte', icon: null, testId: 'support-tab', badge: supportUnreadCount },
              { key: 'account', label: 'Mi Cuenta', icon: null, testId: 'account-tab' },
              { key: 'settings', label: 'Configuracion', icon: null, testId: 'settings-tab' },
            ].map((tab) => (
              <button
                key={tab.key}
                data-testid={tab.testId}
                className={`relative flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-lg text-sm font-medium transition-all whitespace-nowrap border ${
                  activeTab === tab.key
                    ? 'bg-black text-white border-black'
                    : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50 hover:border-gray-300'
                }`}
                onClick={() => tab.key === 'masterclasses' ? navigate('/admin/masterclasses') : setActiveTab(tab.key)}
              >
                {tab.icon}
                {tab.label}
                {tab.badge > 0 && (
                  <span className="absolute -top-1.5 -right-1.5 inline-flex items-center justify-center w-5 h-5 bg-yellow-400 text-black text-xs font-bold rounded-full shadow" data-testid="support-admin-badge">
                    {tab.badge}
                  </span>
                )}
              </button>
            ))}
          </nav>
        </div>

        {/* Questions Tab */}
        {activeTab === 'questions' && (
          <div>
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-gray-900">Gestión de Preguntas</h2>
              <div className="flex space-x-2">
                <button
                  data-testid="reset-leaderboard-button"
                  className="btn-danger flex items-center"
                  onClick={handleResetLeaderboard}
                >
                  <RotateCcw size={16} className="mr-2" />
                  Resetear Ranking
                </button>
                <button
                  data-testid="import-questions-button"
                  className="btn-secondary flex items-center"
                  onClick={() => setShowImportForm(!showImportForm)}
                >
                  📁 {showImportForm ? 'Cancelar' : 'Importar Excel/CSV'}
                </button>
                <button
                  data-testid="create-question-button"
                  className="btn-primary flex items-center"
                  onClick={() => setShowCreateForm(!showCreateForm)}
                >
                  <Plus size={16} className="mr-2" />
                  {showCreateForm ? 'Cancelar' : 'Nueva Pregunta'}
                </button>
              </div>
            </div>

            {/* Import Form */}
            {showImportForm && (
              <div className="bg-white rounded-2xl shadow-lg p-8 mb-8 animate-fadeIn">
                <h3 className="text-xl font-bold text-gray-900 mb-6">Importar Preguntas desde Excel/CSV</h3>
                
                <div className="mb-6 p-6 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border-2 border-blue-300 shadow-md">
                  <h4 className="font-bold text-blue-900 mb-4 text-lg flex items-center">
                    📋 Instrucciones para Importar Preguntas
                  </h4>
                  
                  <div className="bg-white rounded-lg p-4 mb-4">
                    <h5 className="font-semibold text-gray-800 mb-2">Columnas Requeridas (en este orden):</h5>
                    <div className="text-sm text-gray-700 space-y-1 ml-4">
                      <p>1. <strong>question_text</strong> - Texto completo de la pregunta</p>
                      <p>2. <strong>option_a</strong> - Opción A</p>
                      <p>3. <strong>option_b</strong> - Opción B</p>
                      <p>4. <strong>option_c</strong> - Opción C</p>
                      <p>5. <strong>option_d</strong> - Opción D</p>
                      <p>6. <strong>correct_answer</strong> - Respuesta correcta (solo la letra: A, B, C o D)</p>
                      <p>7. <strong>explanation</strong> - Explicación de por qué es correcta</p>
                      <p>8. <strong>reference</strong> - Referencia bibliográfica</p>
                      <p>9. <strong>specialty</strong> - Especialidad médica</p>
                      <p>10. <strong>topic</strong> - Tema específico del cuestionario</p>
                    </div>
                  </div>

                  <div className="bg-white rounded-lg p-4 mb-4">
                    <h5 className="font-semibold text-gray-800 mb-2">Valores Permitidos:</h5>
                    <div className="text-sm text-gray-700 space-y-2">
                      <p><strong>correct_answer:</strong> Solo se permite una letra: <span className="bg-blue-100 px-2 py-1 rounded font-mono">A</span>, <span className="bg-blue-100 px-2 py-1 rounded font-mono">B</span>, <span className="bg-blue-100 px-2 py-1 rounded font-mono">C</span> o <span className="bg-blue-100 px-2 py-1 rounded font-mono">D</span></p>
                      <p><strong>specialty:</strong> Debe ser una de estas opciones exactas:</p>
                      <ul className="ml-6 list-disc">
                        <li>Ginecología y Obstetricia</li>
                        <li>Cirugía</li>
                        <li>Pediatría</li>
                        <li>Medicina Interna</li>
                        <li>Otros</li>
                      </ul>
                    </div>
                  </div>

                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-4">
                    <p className="text-sm text-yellow-800">
                      <strong>⚠️ Importante:</strong> Asegúrate de que todas las columnas estén presentes y en el orden correcto. 
                      No dejes celdas vacías excepto si realmente no hay contenido para ese campo.
                    </p>
                  </div>
                  
                  <button
                    data-testid="download-template-button"
                    className="mt-3 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-semibold transition-colors flex items-center"
                    onClick={downloadTemplate}
                  >
                    📥 Descargar plantilla de ejemplo (CSV)
                  </button>
                </div>

                <form onSubmit={handleImportFile} className="space-y-6">
                  {/* Module and Submodule Selection */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 p-4 bg-teal-50 rounded-lg border border-teal-200">
                    <div>
                      <label className="form-label">Módulo Principal *</label>
                      <select
                        data-testid="import-module-select"
                        className="form-input"
                        value={selectedModule}
                        onChange={(e) => {
                          setSelectedModule(e.target.value);
                          setSelectedSubmodule(''); // Reset submodule when module changes
                        }}
                        required
                      >
                        <option value="">Seleccione un módulo</option>
                        {baseSpecialties.map(specialty => (
                          <option key={specialty} value={specialty}>{specialty}</option>
                        ))}
                      </select>
                      <p className="mt-1 text-xs text-gray-600">
                        Selecciona el módulo al que pertenecen estas preguntas
                      </p>
                    </div>

                    {selectedModule && hasSubmodules(selectedModule) && (
                      <div>
                        <label className="form-label">Submódulo *</label>
                        <select
                          data-testid="import-submodule-select"
                          className="form-input"
                          value={selectedSubmodule}
                          onChange={(e) => setSelectedSubmodule(e.target.value)}
                          required
                        >
                          <option value="">Seleccione un submódulo</option>
                          {getSubmodules(selectedModule).map(submodule => (
                            <option key={submodule} value={submodule}>{submodule}</option>
                          ))}
                        </select>
                        <p className="mt-1 text-xs text-gray-600">
                          Selecciona el submódulo específico
                        </p>
                      </div>
                    )}

                    {selectedModule && !hasSubmodules(selectedModule) && (
                      <div className="flex items-center">
                        <p className="text-sm text-gray-600 italic">
                          ✓ Este módulo no tiene submódulos
                        </p>
                      </div>
                    )}
                  </div>

                  <div>
                    <label className="form-label">Seleccionar Archivo Excel/CSV</label>
                    <input
                      type="file"
                      data-testid="import-file-input"
                      className="form-input"
                      accept=".xlsx,.xls,.csv"
                      onChange={(e) => setImportFile(e.target.files[0])}
                      required
                    />
                    {importFile && (
                      <p className="mt-2 text-sm text-gray-600">
                        Archivo seleccionado: {importFile.name}
                      </p>
                    )}
                  </div>

                  <div className="flex space-x-4">
                    <button
                      type="submit"
                      data-testid="submit-import-button"
                      className="btn-primary flex items-center"
                      disabled={importing}
                    >
                      {importing ? (
                        <>
                          <div className="loading-spinner mr-2"></div>
                          Importando...
                        </>
                      ) : (
                        '📤 Importar Preguntas'
                      )}
                    </button>
                    <button
                      type="button"
                      data-testid="cancel-import-button"
                      className="btn-secondary"
                      onClick={() => {
                        setShowImportForm(false);
                        setImportFile(null);
                        setSelectedModule('');
                        setSelectedSubmodule('');
                      }}
                    >
                      Cancelar
                    </button>
                  </div>
                </form>
              </div>
            )}

            {/* Create Form */}
            {showCreateForm && (
              <div className="bg-white rounded-2xl shadow-lg p-8 mb-8 animate-fadeIn">
                <h3 className="text-xl font-bold text-gray-900 mb-6">Crear Nueva Pregunta</h3>
                <form onSubmit={handleCreateQuestion} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="form-label">Especialidad / Submódulo</label>
                      <select
                        name="specialty"
                        data-testid="specialty-select"
                        className="form-input"
                        value={formData.specialty}
                        onChange={handleInputChange}
                        required
                      >
                        <optgroup label="Ginecología y Obstetricia">
                          <option value="Ginecología y Obstetricia">Ginecología y Obstetricia</option>
                        </optgroup>
                        <optgroup label="Pediatría">
                          <option value="Pediatría">Pediatría</option>
                        </optgroup>
                        <optgroup label="Medicina Interna">
                          {allSpecialtyOptions.filter(opt => opt.startsWith('Medicina Interna')).map(opt => (
                            <option key={opt} value={opt}>{opt}</option>
                          ))}
                        </optgroup>
                        <optgroup label="Cirugía">
                          {allSpecialtyOptions.filter(opt => opt.startsWith('Cirugía')).map(opt => (
                            <option key={opt} value={opt}>{opt}</option>
                          ))}
                        </optgroup>
                        <optgroup label="Otros">
                          {allSpecialtyOptions.filter(opt => opt.startsWith('Otros')).map(opt => (
                            <option key={opt} value={opt}>{opt}</option>
                          ))}
                        </optgroup>
                      </select>
                    </div>
                    <div>
                      <label className="form-label">Tema</label>
                      <input
                        type="text"
                        name="topic"
                        data-testid="topic-input"
                        className="form-input"
                        placeholder="Ej: Hipertensión, Diabetes, etc."
                        value={formData.topic}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <label className="form-label">Pregunta</label>
                    <textarea
                      name="question_text"
                      data-testid="question-text-input"
                      className="form-input"
                      rows={3}
                      placeholder="Escriba la pregunta aquí..."
                      value={formData.question_text}
                      onChange={handleInputChange}
                      required
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="form-label">Opción A</label>
                      <input
                        type="text"
                        name="option_a"
                        data-testid="option-a-input"
                        className="form-input"
                        value={formData.option_a}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                    <div>
                      <label className="form-label">Opción B</label>
                      <input
                        type="text"
                        name="option_b"
                        data-testid="option-b-input"
                        className="form-input"
                        value={formData.option_b}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                    <div>
                      <label className="form-label">Opción C</label>
                      <input
                        type="text"
                        name="option_c"
                        data-testid="option-c-input"
                        className="form-input"
                        value={formData.option_c}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                    <div>
                      <label className="form-label">Opción D</label>
                      <input
                        type="text"
                        name="option_d"
                        data-testid="option-d-input"
                        className="form-input"
                        value={formData.option_d}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <label className="form-label">Respuesta Correcta</label>
                    <select
                      name="correct_answer"
                      data-testid="correct-answer-select"
                      className="form-input"
                      value={formData.correct_answer}
                      onChange={handleInputChange}
                      required
                    >
                      <option value="A">A</option>
                      <option value="B">B</option>
                      <option value="C">C</option>
                      <option value="D">D</option>
                    </select>
                  </div>

                  <div>
                    <label className="form-label">Explicación</label>
                    <textarea
                      name="explanation"
                      data-testid="explanation-input"
                      className="form-input"
                      rows={3}
                      placeholder="Explique por qué esta es la respuesta correcta..."
                      value={formData.explanation}
                      onChange={handleInputChange}
                      required
                    />
                  </div>

                  <div>
                    <label className="form-label">Referencia Bibliográfica</label>
                    <input
                      type="text"
                      name="reference"
                      data-testid="reference-input"
                      className="form-input"
                      placeholder="Ej: Harrison's Principles of Internal Medicine, 20th ed., p. 1234"
                      value={formData.reference}
                      onChange={handleInputChange}
                      required
                    />
                  </div>

                  <div className="flex space-x-4">
                    <button
                      type="submit"
                      data-testid="submit-question-button"
                      className="btn-primary"
                    >
                      Crear Pregunta
                    </button>
                    <button
                      type="button"
                      data-testid="cancel-create-button"
                      className="btn-secondary"
                      onClick={() => {
                        setShowCreateForm(false);
                        resetForm();
                      }}
                    >
                      Cancelar
                    </button>
                  </div>
                </form>
              </div>
            )}

            {/* Filters and Sorting */}
            <div className="bg-white rounded-2xl shadow-lg p-6 mb-8">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Filtros y Ordenamiento</h3>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Especialidad</label>
                  <select
                    data-testid="filter-specialty-select"
                    className="form-input"
                    value={filterSpecialty}
                    onChange={(e) => { setQuestionsPage(0); setFilterSpecialty(e.target.value); }}
                  >
                    <option value="">Todas las especialidades</option>
                    {specialties.map(specialty => (
                      <option key={specialty} value={specialty}>
                        {specialty}
                      </option>
                    ))}
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Tema</label>
                  <input
                    type="text"
                    data-testid="filter-topic-input"
                    className="form-input"
                    placeholder="Filtrar por tema..."
                    value={filterTopic}
                    onChange={(e) => { setQuestionsPage(0); setFilterTopic(e.target.value); }}
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Ordenar por</label>
                  <select
                    data-testid="sort-by-select"
                    className="form-input"
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                  >
                    <option value="created_at">Fecha de creación (más reciente)</option>
                    <option value="specialty">Especialidad (A-Z)</option>
                    <option value="topic">Tema (A-Z)</option>
                  </select>
                </div>
                
                <div className="flex items-end">
                  <button
                    data-testid="clear-filters-button"
                    className="btn-secondary w-full"
                    onClick={() => {
                      setQuestionsPage(0);
                      setFilterSpecialty('');
                      setFilterTopic('');
                      setSortBy('created_at');
                    }}
                  >
                    Limpiar Filtros
                  </button>
                </div>
              </div>
            </div>

            {/* Questions List */}
            {loading ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600 mx-auto"></div>
              </div>
            ) : (
              <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
                <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      <input
                        type="checkbox"
                        checked={selectedQuestions.length === questions.length && questions.length > 0}
                        onChange={handleSelectAllQuestions}
                        className="w-5 h-5 text-teal-600 border-gray-300 rounded focus:ring-teal-500"
                      />
                      <h3 className="text-lg font-semibold text-gray-900">
                        Preguntas Existentes ({questionsTotal.toLocaleString()})
                        {selectedQuestions.length > 0 && (
                          <span className="ml-2 text-teal-600">
                            ({selectedQuestions.length} seleccionada{selectedQuestions.length > 1 ? 's' : ''})
                          </span>
                        )}
                      </h3>
                    </div>
                    {selectedQuestions.length > 0 && (
                      <button
                        onClick={handleDeleteSelected}
                        className="flex items-center px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors font-medium"
                      >
                        <Trash2 size={18} className="mr-2" />
                        Eliminar {selectedQuestions.length} pregunta{selectedQuestions.length > 1 ? 's' : ''}
                      </button>
                    )}
                  </div>
                  {questionsTotal > QUESTIONS_PER_PAGE && (
                    <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-200" data-testid="questions-pagination">
                      <span className="text-sm text-gray-600">
                        Página {questionsPage + 1} de {Math.ceil(questionsTotal / QUESTIONS_PER_PAGE)}
                        <span className="hidden sm:inline"> · mostrando {questions.length} de {questionsTotal.toLocaleString()}</span>
                      </span>
                      <div className="flex items-center gap-2">
                        <button
                          data-testid="questions-prev-page"
                          disabled={questionsPage === 0}
                          onClick={() => { setSelectedQuestions([]); setQuestionsPage(p => Math.max(0, p - 1)); }}
                          className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                          Anterior
                        </button>
                        <button
                          data-testid="questions-next-page"
                          disabled={(questionsPage + 1) >= Math.ceil(questionsTotal / QUESTIONS_PER_PAGE)}
                          onClick={() => { setSelectedQuestions([]); setQuestionsPage(p => p + 1); }}
                          className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                          Siguiente
                        </button>
                      </div>
                    </div>
                  )}
                </div>
                <div className="divide-y divide-gray-200">
                  {questions.map((question) => (
                    <div key={question.id} className="p-6 hover:bg-gray-50 transition-colors">
                      <div className="flex items-start space-x-4">
                        <input
                          type="checkbox"
                          checked={selectedQuestions.includes(question.id)}
                          onChange={() => handleSelectQuestion(question.id)}
                          className="mt-1 w-5 h-5 text-teal-600 border-gray-300 rounded focus:ring-teal-500 cursor-pointer"
                        />
                        <div className="flex-1">
                          <div className="flex items-center space-x-4 mb-2">
                            <span className="bg-teal-100 text-teal-800 px-3 py-1 rounded-full text-sm font-medium">
                              {question.specialty}
                            </span>
                            <span className="bg-gray-100 text-gray-800 px-3 py-1 rounded-full text-sm">
                              {question.topic}
                            </span>
                          </div>
                          <h4 className="text-lg font-semibold text-gray-900 mb-2">
                            {question.question_text}
                          </h4>
                          <p className="text-sm text-gray-600 mb-2">
                            <strong>Respuesta correcta:</strong> {question.correct_answer} - {question[`option_${question.correct_answer.toLowerCase()}`]}
                          </p>
                          <p className="text-sm text-gray-500">
                            {question.explanation.substring(0, 100)}...
                          </p>
                          <button
                            data-testid={`delete-question-${question.id}`}
                            className="mt-2 text-red-600 hover:text-red-800 transition-colors inline-flex items-center text-sm"
                            onClick={() => handleDeleteQuestion(question.id)}
                          >
                            <Trash2 size={16} className="mr-1" />
                            Eliminar
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Users Tab */}
        {activeTab === 'users' && (
          <div>
            {/* Pending Users Section */}
            <div className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Usuarios Pendientes de Aprobación</h2>
              
              {loading ? (
                <div className="text-center py-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600 mx-auto"></div>
                </div>
              ) : pendingUsers.length === 0 ? (
                <div className="bg-white rounded-2xl shadow-lg p-8 text-center">
                  <Users size={48} className="mx-auto text-gray-400 mb-3" />
                  <p className="text-gray-600">No hay usuarios pendientes de aprobación</p>
                </div>
              ) : (
                <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
                  <div className="divide-y divide-gray-200">
                    {pendingUsers.map((user) => (
                      <div key={user.id} className="p-6">
                        <div className="flex justify-between items-center">
                          <div>
                            <h4 className="text-lg font-semibold text-gray-900">
                              {user.full_name}
                            </h4>
                            <p className="text-gray-600">{user.email}</p>
                          </div>
                          <div className="flex space-x-2">
                            <button
                              data-testid={`approve-user-${user.id}`}
                              className="flex items-center px-4 py-2 bg-green-100 text-green-800 rounded-lg hover:bg-green-200 transition-colors"
                              onClick={() => handleApproveUser(user.id)}
                            >
                              <Check size={16} className="mr-2" />
                              Aprobar
                            </button>
                            <button
                              data-testid={`reject-user-${user.id}`}
                              className="flex items-center px-4 py-2 bg-red-100 text-red-800 rounded-lg hover:bg-red-200 transition-colors"
                              onClick={() => setUserAction({ userId: user.id, userName: user.full_name, type: 'reject' })}
                            >
                              <X size={16} className="mr-2" />
                              Rechazar
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Approved Users Section */}
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Usuarios Aprobados</h2>
              
              {loading ? (
                <div className="text-center py-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600 mx-auto"></div>
                </div>
              ) : approvedUsers.length === 0 ? (
                <div className="bg-white rounded-2xl shadow-lg p-8 text-center">
                  <Users size={48} className="mx-auto text-gray-400 mb-3" />
                  <p className="text-gray-600">No hay usuarios aprobados</p>
                </div>
              ) : (
                <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
                  <div className="divide-y divide-gray-200">
                    {approvedUsers.map((user) => (
                      <div key={user.id} className="p-6">
                        <div className="flex justify-between items-center">
                          <div className="flex items-center space-x-4">
                            {user.profile_image && (
                              <img 
                                src={user.profile_image} 
                                alt={user.full_name}
                                className="w-12 h-12 rounded-full object-cover"
                              />
                            )}
                            <div>
                              <h4 className="text-lg font-semibold text-gray-900">
                                {user.full_name}
                              </h4>
                              <p className="text-gray-600">{user.email}</p>
                            </div>
                          </div>
                          <button
                            data-testid={`delete-user-${user.id}`}
                            className="flex items-center px-4 py-2 bg-red-100 text-red-800 rounded-lg hover:bg-red-200 transition-colors"
                            onClick={() => setUserAction({ userId: user.id, userName: user.full_name, type: 'delete' })}
                          >
                            <Trash2 size={16} className="mr-2" />
                            Eliminar
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Clinical Cases Tab */}
        {activeTab === 'cases' && (
          <div>
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-gray-900">Gestión de Casos Clínicos</h2>
            </div>

            {/* Upload Form */}
            <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
              <h3 className="text-lg font-semibold mb-4">Subir Caso Clínico</h3>
              <form onSubmit={async (e) => {
                e.preventDefault();
                const formData = new FormData(e.target);
                try {
                  setImporting(true);
                  const response = await apiClient.post('/admin/cases/upload', formData, {
                    headers: {
                      'Content-Type': 'multipart/form-data',
                    },
                  });
                  toast.success('Caso clínico subido exitosamente');
                  fetchData();
                  e.target.reset();
                } catch (error) {
                  toast.error(error.response?.data?.detail || 'Error al subir caso clínico');
                } finally {
                  setImporting(false);
                }
              }}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Título del Caso
                    </label>
                    <input
                      type="text"
                      name="title"
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                      placeholder="Ej: Glomerulonefritis lúpica..."
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Tema
                    </label>
                    <select
                      name="theme"
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                    >
                      <option value="Cirugía">Cirugía</option>
                      <option value="Medicina Interna">Medicina Interna</option>
                      <option value="Pediatría">Pediatría</option>
                      <option value="Ginecología y Obstetricia">Ginecología y Obstetricia</option>
                      <option value="Otros">Otros</option>
                    </select>
                  </div>
                </div>
                
                {/* Módulo y Submódulo */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4 p-4 bg-teal-50 rounded-lg border border-teal-200">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Módulo *
                    </label>
                    <select
                      name="module"
                      required
                      value={selectedModule}
                      onChange={(e) => {
                        setSelectedModule(e.target.value);
                        setSelectedSubmodule('');
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                    >
                      <option value="">Seleccione un módulo</option>
                      {baseSpecialties.map(specialty => (
                        <option key={specialty} value={specialty}>{specialty}</option>
                      ))}
                    </select>
                  </div>
                  
                  {selectedModule && hasSubmodules(selectedModule) && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Submódulo *
                      </label>
                      <select
                        name="submodule"
                        required
                        value={selectedSubmodule}
                        onChange={(e) => setSelectedSubmodule(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                      >
                        <option value="">Seleccione un submódulo</option>
                        {getSubmodules(selectedModule).map(submodule => (
                          <option key={submodule} value={submodule}>{submodule}</option>
                        ))}
                      </select>
                    </div>
                  )}
                  
                  {selectedModule && !hasSubmodules(selectedModule) && (
                    <div className="flex items-center">
                      <p className="text-sm text-gray-600 italic">
                        ✓ Este módulo no tiene submódulos
                      </p>
                      <input type="hidden" name="submodule" value="General" />
                    </div>
                  )}
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Descripción
                  </label>
                  <textarea
                    name="description"
                    required
                    rows="3"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                    placeholder="Breve descripción del caso clínico..."
                  />
                </div>
                <div className="mb-6 p-6 bg-gradient-to-r from-emerald-50 to-teal-50 rounded-lg border-2 border-emerald-300 shadow-md">
                  <h5 className="font-bold text-emerald-900 mb-4 text-lg">📋 Instrucciones del Archivo CSV/Excel para Casos Clínicos</h5>
                  
                  {/* Estructura del archivo */}
                  <div className="bg-white rounded-lg p-4 mb-4">
                    <h6 className="font-semibold text-gray-800 mb-3">Estructura del archivo CSV/Excel:</h6>
                    <ul className="text-sm text-gray-700 space-y-2 ml-4 list-disc">
                      <li><strong>7 pasos principales</strong> con preguntas y alternativas</li>
                      <li>Cada paso incluye <strong>pregunta principal + rama alternativa</strong></li>
                      <li><strong>Mensajes personalizados</strong> para cada respuesta</li>
                      <li><strong>Mensajes globales</strong> de curación y muerte</li>
                    </ul>
                  </div>

                  {/* Orden exacto de columnas */}
                  <div className="bg-white rounded-lg p-4 mb-4">
                    <h6 className="font-semibold text-gray-800 mb-3">⚠️ NOMBRES EXACTOS de columnas (CSV/Excel):</h6>
                    <div className="text-sm text-gray-700 space-y-1">
                      <div className="grid grid-cols-2 gap-x-4 gap-y-1">
                        <p>1. <span className="bg-emerald-100 px-2 py-0.5 rounded font-mono text-xs">Paso</span></p>
                        <p>2. <span className="bg-emerald-100 px-2 py-0.5 rounded font-mono text-xs">Pregunta_Principal</span></p>
                        <p>3. <span className="bg-emerald-100 px-2 py-0.5 rounded font-mono text-xs">A_Principal</span></p>
                        <p>4. <span className="bg-emerald-100 px-2 py-0.5 rounded font-mono text-xs">B_Principal</span></p>
                        <p>5. <span className="bg-emerald-100 px-2 py-0.5 rounded font-mono text-xs">C_Principal</span></p>
                        <p>6. <span className="bg-emerald-100 px-2 py-0.5 rounded font-mono text-xs">D_Principal</span></p>
                        <p>7. <span className="bg-emerald-100 px-2 py-0.5 rounded font-mono text-xs">RespuestaCorrecta_Principal</span></p>
                        <p>8. <span className="bg-emerald-100 px-2 py-0.5 rounded font-mono text-xs">MensajeCorrecto_Principal</span></p>
                        <p>9. <span className="bg-emerald-100 px-2 py-0.5 rounded font-mono text-xs">MensajeIncorrecto_Principal</span></p>
                        <p>10. <span className="bg-blue-100 px-2 py-0.5 rounded font-mono text-xs">Pregunta_Alternativa</span></p>
                        <p>11. <span className="bg-blue-100 px-2 py-0.5 rounded font-mono text-xs">A_Alternativa</span></p>
                        <p>12. <span className="bg-blue-100 px-2 py-0.5 rounded font-mono text-xs">B_Alternativa</span></p>
                        <p>13. <span className="bg-blue-100 px-2 py-0.5 rounded font-mono text-xs">C_Alternativa</span></p>
                        <p>14. <span className="bg-blue-100 px-2 py-0.5 rounded font-mono text-xs">D_Alternativa</span></p>
                        <p>15. <span className="bg-blue-100 px-2 py-0.5 rounded font-mono text-xs">RespuestaCorrecta_Alternativa</span></p>
                        <p>16. <span className="bg-blue-100 px-2 py-0.5 rounded font-mono text-xs">MensajeCorrecto_Alternativa</span></p>
                        <p>17. <span className="bg-purple-100 px-2 py-0.5 rounded font-mono text-xs">MensajeCuracion</span></p>
                        <p>18. <span className="bg-purple-100 px-2 py-0.5 rounded font-mono text-xs">MensajeMuerte</span></p>
                      </div>
                    </div>
                  </div>

                  {/* Nota sobre columnas 17 y 18 */}
                  <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 mb-4">
                    <p className="text-sm text-purple-800">
                      <strong>📌 Nota:</strong> Las columnas 17 y 18 (<span className="font-mono">MensajeCuracion</span> y <span className="font-mono">MensajeMuerte</span>) 
                      <strong> solo se llenan en la primera fila</strong>.
                    </p>
                  </div>

                  {/* Lógica del juego */}
                  <div className="bg-white rounded-lg p-4 mb-4">
                    <h6 className="font-semibold text-gray-800 mb-3">🎮 Lógica del juego:</h6>
                    <div className="text-sm text-gray-700 space-y-2">
                      <div className="flex items-start space-x-2">
                        <span className="text-green-600 font-bold">•</span>
                        <p><strong>0-1 error total</strong> = MensajeCuracion (aprobado)</p>
                      </div>
                      <div className="flex items-start space-x-2">
                        <span className="text-red-600 font-bold">•</span>
                        <p><strong>2+ errores totales</strong> = MensajeMuerte (reprobado inmediatamente)</p>
                      </div>
                      <div className="flex items-start space-x-2">
                        <span className="text-red-600 font-bold">•</span>
                        <p><strong>&gt;7 pasos</strong> = muerte automática</p>
                      </div>
                      <div className="flex items-start space-x-2">
                        <span className="text-green-600 font-bold">•</span>
                        <p><strong>Completar en ≤7 pasos con ≤1 error</strong> = éxito</p>
                      </div>
                    </div>
                  </div>

                  {/* Advertencia final */}
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                    <p className="text-sm text-yellow-800">
                      <strong>⚠️ Importante:</strong> Todos los pasos deben tener pregunta principal. 
                      Las preguntas alternativas son obligatorias para dar segunda oportunidad al usuario.
                    </p>
                  </div>
                </div>

                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Archivo CSV o Excel (.csv, .xlsx, .xls)
                  </label>
                  <input
                    type="file"
                    name="file"
                    required
                    accept=".csv,.xlsx,.xls"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  />
                </div>
                <button
                  type="submit"
                  disabled={importing}
                  className="btn-primary w-full md:w-auto"
                >
                  {importing ? 'Subiendo...' : 'Subir Caso Clínico'}
                </button>
              </form>
            </div>

            {/* Cases List */}
            <div className="bg-white rounded-lg shadow-sm overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <input
                      type="checkbox"
                      checked={selectedCases.length === clinicalCases.length && clinicalCases.length > 0}
                      onChange={handleSelectAllCases}
                      className="w-5 h-5 text-teal-600 border-gray-300 rounded focus:ring-teal-500"
                    />
                    <h3 className="text-lg font-semibold">
                      Casos Clínicos Disponibles ({clinicalCases.length})
                      {selectedCases.length > 0 && (
                        <span className="ml-2 text-teal-600">
                          ({selectedCases.length} seleccionado{selectedCases.length > 1 ? 's' : ''})
                        </span>
                      )}
                    </h3>
                  </div>
                  {selectedCases.length > 0 && !ccBalance?.running && (
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => setShowCcConfirm(true)}
                        data-testid="start-cc-balance-button"
                        className="flex items-center px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition-colors font-medium"
                      >
                        Balancear {selectedCases.length} con IA
                      </button>
                      <button
                        onClick={openDeleteCasesModal}
                        className="flex items-center px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors font-medium"
                      >
                        <Trash2 size={18} className="mr-2" />
                        Eliminar {selectedCases.length} caso{selectedCases.length > 1 ? 's' : ''}
                      </button>
                    </div>
                  )}
                </div>
              </div>
              {ccBalance?.running && (
                <div className="px-6 py-3 bg-indigo-50 border-b border-indigo-200 flex items-center gap-3" data-testid="cc-balance-progress">
                  <div className="animate-spin w-5 h-5 border-2 border-indigo-300 border-t-indigo-600 rounded-full"></div>
                  <span className="text-sm font-medium text-indigo-800">
                    Balanceando con IA — {ccBalance.processed_cases}/{ccBalance.total_cases} caso(s) · {ccBalance.questions_fixed} preguntas ajustadas
                  </span>
                </div>
              )}
              {loading ? (
                <div className="p-8 text-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600 mx-auto"></div>
                </div>
              ) : clinicalCases.length === 0 ? (
                <div className="p-8 text-center text-gray-500">
                  No hay casos clínicos disponibles
                </div>
              ) : (
                <div className="divide-y divide-gray-200">
                  {clinicalCases.map((clinicalCase) => (
                    <div key={clinicalCase.id} className="p-6 hover:bg-gray-50">
                      <div className="flex items-start space-x-4">
                        <input
                          type="checkbox"
                          checked={selectedCases.includes(clinicalCase.id)}
                          onChange={() => handleSelectCase(clinicalCase.id)}
                          className="mt-1 w-5 h-5 text-teal-600 border-gray-300 rounded focus:ring-teal-500 cursor-pointer"
                        />
                        <div className="flex-1">
                          <h4 className="text-lg font-semibold text-gray-900 mb-2 flex items-center gap-2">
                            {clinicalCase.title}
                            {clinicalCase.options_balanced && (
                              <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-green-100 text-green-700" data-testid={`cc-balanced-badge-${clinicalCase.id}`}>
                                Balanceado
                              </span>
                            )}
                          </h4>
                          <p className="text-sm text-gray-600 mb-2">
                            {clinicalCase.description}
                          </p>
                          <div className="flex items-center space-x-4 text-sm text-gray-500">
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                              {clinicalCase.theme}
                            </span>
                            <span>{clinicalCase.steps_count ?? clinicalCase.steps?.length ?? 0} pasos</span>
                            <span className="text-xs">
                              Creado: {new Date(clinicalCase.created_at).toLocaleDateString()}
                            </span>
                          </div>
                          <button
                            className="mt-2 text-red-600 hover:text-red-800 transition-colors inline-flex items-center text-sm"
                            onClick={async () => {
                              if (window.confirm('¿Estás seguro de eliminar este caso clínico?')) {
                                try {
                                  await apiClient.delete(`/admin/cases/${clinicalCase.id}`);
                                  toast.success('Caso clínico eliminado correctamente');
                                  await fetchData();
                                } catch (error) {
                                  toast.error(error.response?.data?.detail || 'Error al eliminar caso clínico');
                                }
                              }
                            }}
                          >
                            <Trash2 size={16} className="mr-1" />
                            Eliminar
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Imagen DX Tab */}
        {activeTab === 'imagendx' && (
          <div>
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-gray-900">Gestión de Imagen DX</h2>
            </div>

            {/* Upload Form */}
            <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
              <h3 className="text-lg font-semibold mb-4">Subir Casos de Imagen DX</h3>
              <form onSubmit={async (e) => {
                e.preventDefault();
                
                if (!selectedSystem) {
                  toast.error('Debe seleccionar un sistema');
                  return;
                }
                
                if (!imagenDXFile) {
                  toast.error('Debe seleccionar un archivo CSV/Excel');
                  return;
                }
                
                try {
                  setImporting(true);
                  
                  // Step 1: Upload CSV/Excel file
                  const formData = new FormData();
                  formData.append('file', imagenDXFile);
                  formData.append('system', selectedSystem);
                  formData.append('replace_existing', 'false');
                  
                  const uploadResponse = await apiClient.post('/admin/imagendx/upload', formData, {
                    headers: {
                      'Content-Type': 'multipart/form-data',
                    },
                  });
                  
                  toast.success(`${uploadResponse.data.imported_count} casos importados exitosamente`);
                  
                  // Step 2: Upload images if any
                  const uploadedCases = await apiClient.get('/imagendx/cases', {
                    params: { system: selectedSystem }
                  });
                  
                  // Upload images for each case
                  for (const [caseId, imageFile] of Object.entries(imagenDXImages)) {
                    if (imageFile) {
                      const imageFormData = new FormData();
                      imageFormData.append('file', imageFile);
                      
                      try {
                        await apiClient.post(`/admin/imagendx/upload-image/${caseId}`, imageFormData, {
                          headers: {
                            'Content-Type': 'multipart/form-data',
                          },
                        });
                      } catch (imgError) {
                        console.error('Error uploading image for case:', caseId, imgError);
                      }
                    }
                  }
                  
                  // Reset form
                  setSelectedSystem('');
                  setImagenDXFile(null);
                  setImagenDXImages({});
                  e.target.reset();
                  
                  // Reload data
                  fetchData();
                  
                } catch (error) {
                  console.error('Error uploading Imagen DX cases:', error);
                  toast.error(error.response?.data?.detail || 'Error al subir casos de Imagen DX');
                } finally {
                  setImporting(false);
                }
              }}>
                <div className="grid grid-cols-1 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Sistema / Subtema *
                    </label>
                    <select
                      value={selectedSystem}
                      onChange={(e) => setSelectedSystem(e.target.value)}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                    >
                      <option value="">Seleccione un sistema</option>
                      <option value="Tórax">Tórax</option>
                      <option value="Abdomen">Abdomen</option>
                      <option value="Neuro">Neuro</option>
                      <option value="MSK">MSK</option>
                      <option value="Cardio">Cardio</option>
                      <option value="Vascular">Vascular</option>
                      <option value="ORL">ORL</option>
                      <option value="Pediatría">Pediatría</option>
                      <option value="Gineco">Gineco</option>
                      <option value="Urinario">Urinario</option>
                      <option value="Hígado">Hígado</option>
                      <option value="Infeccioso">Infeccioso</option>
                      <option value="Tiroides">Tiroides</option>
                      <option value="Mama">Mama</option>
                      <option value="Pulmón">Pulmón</option>
                      <option value="Biliar">Biliar</option>
                      <option value="Renal">Renal</option>
                      <option value="Endocrino">Endocrino</option>
                      <option value="Gastro">Gastro</option>
                      <option value="Trauma">Trauma</option>
                      <option value="Páncreas">Páncreas</option>
                      <option value="Hematología">Hematología</option>
                      <option value="Bazo">Bazo</option>
                      <option value="Dermatología">Dermatología</option>
                      <option value="Patología">Patología</option>
                    </select>
                  </div>
                </div>
                
                <div className="mb-6 p-6 bg-gradient-to-r from-blue-50 to-cyan-50 rounded-lg border-2 border-blue-300 shadow-md">
                  <h5 className="font-bold text-blue-900 mb-4 text-lg">📋 Instrucciones del Archivo CSV/Excel para Imagen DX</h5>
                  
                  <div className="bg-white rounded-lg p-4 mb-4">
                    <h6 className="font-semibold text-gray-800 mb-3">Columnas requeridas:</h6>
                    <ul className="text-sm text-gray-700 space-y-2 ml-4 list-disc">
                      <li><strong>system</strong> - Sistema/Subtema (Tórax, Abdomen, Dermatología, Patología, etc.)</li>
                      <li><strong>modality</strong> - Modalidad (radiografía, TC, RM, etc.)</li>
                      <li><strong>finding_or_sign</strong> - Hallazgo o signo correcto</li>
                      <li><strong>interpretation_clave</strong> - Interpretación clave</li>
                      <li><strong>diagnostico_sugerido</strong> - Diagnóstico sugerido</li>
                      <li><strong>CASO CLINICO</strong> - Texto del caso clínico</li>
                      <li><strong>A, B, C, D</strong> - Cuatro opciones de respuesta</li>
                      <li><strong>link_imagen_referencia</strong> - Link de la imagen (opcional)</li>
                    </ul>
                  </div>
                  
                  <div className="bg-white rounded-lg p-4">
                    <p className="text-sm text-gray-700">
                      <strong>Nota:</strong> El sistema seleccionado arriba se aplicará a todos los casos del archivo.
                      La opción correcta debe coincidir exactamente con el campo <strong>finding_or_sign</strong>.
                    </p>
                  </div>
                </div>
                
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Archivo CSV/Excel *
                  </label>
                  <input
                    type="file"
                    accept=".csv,.xlsx,.xls"
                    onChange={(e) => setImagenDXFile(e.target.files[0])}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500"
                  />
                </div>
                
                <button
                  type="submit"
                  disabled={importing || !selectedSystem || !imagenDXFile}
                  className="w-full bg-teal-600 text-white py-3 rounded-lg hover:bg-teal-700 disabled:bg-gray-400 disabled:cursor-not-allowed font-medium"
                >
                  {importing ? 'Subiendo...' : 'Subir Casos de Imagen DX'}
                </button>
              </form>
            </div>

            {/* Cases List */}
            <div className="bg-white rounded-lg shadow-sm">
              <div className="p-6 border-b border-gray-200 bg-gray-50">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <input
                      type="checkbox"
                      checked={selectedImagenDX.length === imagenDXCases.length && imagenDXCases.length > 0}
                      onChange={handleSelectAllImagenDX}
                      className="w-5 h-5 text-teal-600 border-gray-300 rounded focus:ring-teal-500"
                    />
                    <h3 className="text-lg font-semibold">
                      Casos de Imagen DX ({imagenDXCases.length})
                      {selectedImagenDX.length > 0 && (
                        <span className="ml-2 text-teal-600">
                          ({selectedImagenDX.length} seleccionado{selectedImagenDX.length > 1 ? 's' : ''})
                        </span>
                      )}
                    </h3>
                  </div>
                  {selectedImagenDX.length > 0 && (
                    <button
                      onClick={openDeleteImagenDXModal}
                      className="flex items-center px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors font-medium"
                    >
                      <Trash2 size={18} className="mr-2" />
                      Eliminar {selectedImagenDX.length} caso{selectedImagenDX.length > 1 ? 's' : ''}
                    </button>
                  )}
                </div>
              </div>
              {loading ? (
                <div className="p-8 text-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600 mx-auto"></div>
                </div>
              ) : imagenDXCases.length === 0 ? (
                <div className="p-8 text-center text-gray-500">
                  No hay casos de Imagen DX disponibles
                </div>
              ) : (
                <div className="divide-y divide-gray-200">
                  {imagenDXCases.map((imgCase) => (
                    <div key={imgCase.id} className="p-6 hover:bg-gray-50">
                      <div className="flex items-start space-x-4">
                        <input
                          type="checkbox"
                          checked={selectedImagenDX.includes(imgCase.id)}
                          onChange={() => handleSelectImagenDX(imgCase.id)}
                          className="mt-1 w-5 h-5 text-teal-600 border-gray-300 rounded focus:ring-teal-500 cursor-pointer"
                        />
                        <div className="flex-1">
                          <h4 className="text-lg font-semibold text-gray-900 mb-2">
                            {imgCase.finding_or_sign}
                          </h4>
                          <p className="text-sm text-gray-600 mb-2">
                            {imgCase.caso_clinico?.substring(0, 150)}...
                          </p>
                          <div className="flex items-center space-x-4 text-sm text-gray-500">
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                              {imgCase.system}
                            </span>
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                              {imgCase.modality}
                            </span>
                            <span className="text-xs">
                              {imgCase.image_urls?.length > 1 
                                ? `✓ ${imgCase.image_urls.length} imágenes` 
                                : imgCase.image_url 
                                  ? '✓ Con imagen' 
                                  : '⚠ Sin imagen'}
                            </span>
                            <span className="text-xs">
                              Creado: {new Date(imgCase.created_at).toLocaleDateString()}
                            </span>
                          </div>
                          
                          <div className="mt-3 flex items-center space-x-3">
                            {/* Upload image(s) button - multiple for Dermatología/Patología */}
                            {!imgCase.image_url && (
                              <label className="inline-flex items-center px-3 py-1.5 bg-blue-50 text-blue-700 text-sm rounded-lg cursor-pointer hover:bg-blue-100">
                                📤 {['Dermatología', 'Patología'].includes(imgCase.system) ? 'Subir Imágenes (hasta 3)' : 'Subir Imagen'}
                                <input
                                  type="file"
                                  accept="image/*"
                                  multiple={['Dermatología', 'Patología'].includes(imgCase.system)}
                                  className="hidden"
                                  onChange={async (e) => {
                                    const files = Array.from(e.target.files);
                                    if (!files.length) return;
                                    
                                    try {
                                      const isMultipleSystem = ['Dermatología', 'Patología'].includes(imgCase.system);
                                      
                                      if (isMultipleSystem && files.length > 1) {
                                        // Upload multiple images
                                        if (files.length > 3) {
                                          toast.error('Máximo 3 imágenes permitidas');
                                          return;
                                        }
                                        
                                        const formData = new FormData();
                                        files.forEach((file) => {
                                          formData.append('files', file);
                                        });
                                        
                                        await apiClient.post(`/admin/imagendx/upload-multiple-images/${imgCase.id}`, formData, {
                                          headers: {
                                            'Content-Type': 'multipart/form-data',
                                          },
                                        });
                                        
                                        toast.success(`${files.length} imágenes subidas exitosamente`);
                                      } else {
                                        // Upload single image
                                        const formData = new FormData();
                                        formData.append('file', files[0]);
                                        
                                        await apiClient.post(`/admin/imagendx/upload-image/${imgCase.id}`, formData, {
                                          headers: {
                                            'Content-Type': 'multipart/form-data',
                                          },
                                        });
                                        
                                        toast.success('Imagen subida exitosamente');
                                      }
                                      
                                      fetchData();
                                    } catch (error) {
                                      console.error('Error uploading image:', error);
                                      toast.error(error.response?.data?.detail || 'Error al subir imagen');
                                    }
                                  }}
                                />
                              </label>
                            )}
                            
                            {/* Delete button */}
                            <button
                              className="text-red-600 hover:text-red-800 transition-colors inline-flex items-center text-sm"
                              onClick={async (e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                if (window.confirm('¿Estás seguro de eliminar este caso de Imagen DX?')) {
                                  try {
                                    await apiClient.delete(`/admin/imagendx/cases/${imgCase.id}`);
                                    toast.success('Caso eliminado correctamente');
                                    const casesResponse = await apiClient.get('/imagendx/cases');
                                    setImagenDXCases(casesResponse.data);
                                  } catch (error) {
                                    toast.error(error.response?.data?.detail || 'Error al eliminar caso');
                                  }
                                }
                              }}
                            >
                              <Trash2 size={16} className="mr-1" />
                              Eliminar
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Presentations Tab */}
        {activeTab === 'presentations' && (
          <PresentationsAdminTab />
        )}

        {/* Duel Questions Tab */}
        {activeTab === 'duel-questions' && (
          <DuelQuestionsAdminTab />
        )}

        {/* Simulacros Tab */}
        {activeTab === 'simulacros' && (
          <SimulacrosAdminTab />
        )}

        {/* Card Game Tab */}
        {activeTab === 'card-game' && (
          <CardGameAdminTab />
        )}

        {/* Admin Points Tab */}
        {activeTab === 'admin-points' && (
          <AdminPointsPanel apiClient={apiClient} user={user} onUserUpdate={onUserUpdate} />
        )}

        {/* Usage Report Tab */}
        {activeTab === 'usage' && (
          <UserUsageAdmin apiClient={apiClient} />
        )}
        {activeTab === 'user-access' && (
          <UserAccessAdmin apiClient={apiClient} />
        )}

        {/* Planner Order Tab */}
        {activeTab === 'planner-order' && (
          <PlannerOrderAdmin apiClient={apiClient} />
        )}

        {activeTab === 'journal' && (
          <JournalPanel apiClient={apiClient} />
        )}

        {/* Support Tab */}
        {activeTab === 'support' && (
          <SupportAdminPanel
            apiClient={apiClient}
            tickets={supportTickets}
            expandedTicket={supportExpandedTicket}
            setExpandedTicket={setSupportExpandedTicket}
            replyText={supportReplyText}
            setReplyText={setSupportReplyText}
            onRefresh={fetchData}
          />
        )}

        {/* Account Tab */}
        {activeTab === 'account' && (
          <AdminAccountPanel apiClient={apiClient} />
        )}

        {activeTab === 'settings' && (
          <AdminSettingsPanel apiClient={apiClient} />
        )}
      </div>

      {/* Modal de confirmación de usuario (rechazar/eliminar) */}
      {userAction && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" data-testid="user-action-modal">
          <div className="bg-white rounded-xl p-6 max-w-md mx-4 shadow-2xl">
            <h3 className="text-lg font-bold text-gray-900 mb-2">
              {userAction.type === 'reject' ? '¿Rechazar usuario?' : '¿Eliminar usuario?'}
            </h3>
            <p className="text-gray-600 mb-4">
              {userAction.type === 'reject'
                ? `¿Estás seguro de que quieres rechazar a "${userAction.userName}"? Su cuenta será eliminada.`
                : `¿Estás seguro de que quieres eliminar a "${userAction.userName}"? Esta acción no se puede deshacer.`
              }
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setUserAction(null)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 font-medium"
                data-testid="user-action-cancel"
              >
                Cancelar
              </button>
              <button
                onClick={() => handleRejectUser(userAction.userId)}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 font-semibold"
                data-testid="user-action-confirm"
              >
                {userAction.type === 'reject' ? 'Sí, rechazar' : 'Sí, eliminar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de confirmación de eliminación */}
      {showCcConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" data-testid="cc-balance-confirm-modal">
          <div className="bg-white rounded-2xl p-8 max-w-md mx-4 shadow-2xl">
            <h3 className="text-xl font-bold text-gray-900 mb-2">¿Balancear opciones con IA?</h3>
            <p className="text-gray-600 mb-6 text-sm">
              Se procesarán <strong>{selectedCases.length} caso(s) clínico(s)</strong> seleccionados con IA (Claude Sonnet 4.6) para igualar la longitud de las opciones (pregunta Principal y Alternativa). Se crea un respaldo automático antes de modificar. Los casos ya balanceados se omiten. Esto consume saldo de la Emergent LLM Key.
            </p>
            <div className="flex space-x-4">
              <button
                onClick={() => setShowCcConfirm(false)}
                className="flex-1 px-4 py-3 bg-gray-200 text-gray-800 font-semibold rounded-lg hover:bg-gray-300 transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={handleStartCcBalance}
                data-testid="confirm-cc-balance-button"
                className="flex-1 px-4 py-3 bg-indigo-600 text-white font-semibold rounded-lg hover:bg-indigo-700 transition-colors"
              >
                Sí, iniciar
              </button>
            </div>
          </div>
        </div>
      )}

      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-8 max-w-md mx-4 shadow-2xl">
            <div className="text-center">
              <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-red-100 mb-4">
                <Trash2 className="h-8 w-8 text-red-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">
                Confirmar Eliminación
              </h3>
              <p className="text-gray-600 mb-6">
                ¿Estás seguro de eliminar{' '}
                <span className="font-bold text-red-600">
                  {deleteType === 'cases' ? selectedCases.length : selectedImagenDX.length}
                </span>{' '}
                {deleteType === 'cases' ? 'caso(s) clínico(s)' : 'caso(s) de Imagen DX'}?
                <br />
                <span className="text-sm text-red-500 font-medium">
                  Esta acción no se puede deshacer.
                </span>
              </p>
              <div className="flex space-x-4">
                <button
                  onClick={() => setShowDeleteModal(false)}
                  disabled={deleting}
                  className="flex-1 px-4 py-3 bg-gray-200 text-gray-800 font-semibold rounded-lg hover:bg-gray-300 transition-colors"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleConfirmDelete}
                  disabled={deleting}
                  className="flex-1 px-4 py-3 bg-red-600 text-white font-semibold rounded-lg hover:bg-red-700 transition-colors flex items-center justify-center"
                >
                  {deleting ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                      Eliminando...
                    </>
                  ) : (
                    <>
                      <Trash2 size={18} className="mr-2" />
                      Sí, Eliminar
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ==================== PRESENTATIONS ADMIN TAB ====================
const PresentationsAdminTab = () => {
  const [presentations, setPresentations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [showUploadForm, setShowUploadForm] = useState(false);
  const [reuploadingId, setReuploadingId] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    title: '',
    module: '',
    submodule: '',
    description: ''
  });
  const [selectedFile, setSelectedFile] = useState(null);
  
  // Module structure
  const MODULES = {
    "Ginecología y Obstetricia": { hasSubmodules: false, submodules: [] },
    "Pediatría": { hasSubmodules: false, submodules: [] },
    "Cirugía": {
      hasSubmodules: true,
      submodules: [
        "Angiología", "Cirugía General", "Cirugía Maxilofacial", "Cirugía Plástica",
        "Neurocirugía", "Oftalmología", "Otorrinolaringología", "Trasplantes",
        "Traumatología y Ortopedia", "Urología"
      ]
    },
    "Medicina Interna": {
      hasSubmodules: true,
      submodules: [
        "Cardiología", "Dermatología", "Endocrinología", "Gastroenterología",
        "Hematología", "Infectología", "Medicina Interna (general)", "Nefrología",
        "Neumología", "Neurología", "Oncología", "Reumatología"
      ]
    },
    "Otros": {
      hasSubmodules: true,
      submodules: [
        "Alergia e Inmunología", "Analgesia y Anestesia", "Enfermedades Lisosomales",
        "Genética", "Geriatría", "Medicina Familiar", "Medicina Física y Rehabilitación",
        "Nutriología", "Psiquiatría", "Urgencias Médico-Quirúrgicas"
      ]
    }
  };

  useEffect(() => {
    fetchPresentations();
  }, []);

  const fetchPresentations = async () => {
    try {
      const response = await apiClient.get('/admin/presentations/all');
      setPresentations(response.data);
    } catch (error) {
      toast.error('Error al cargar presentaciones');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleModuleChange = (e) => {
    const module = e.target.value;
    setFormData({
      ...formData,
      module,
      submodule: '' // Reset submodule when module changes
    });
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file && file.type === 'application/pdf') {
      setSelectedFile(file);
    } else {
      toast.error('Solo se permiten archivos PDF');
      e.target.value = '';
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title || !formData.module || !selectedFile) {
      toast.error('Por favor completa todos los campos requeridos');
      return;
    }
    
    // Check if submodule is required
    if (MODULES[formData.module]?.hasSubmodules && !formData.submodule) {
      toast.error('Por favor selecciona un submódulo');
      return;
    }
    
    setUploading(true);
    
    try {
      const data = new FormData();
      data.append('title', formData.title);
      data.append('module', formData.module);
      if (formData.submodule) {
        data.append('submodule', formData.submodule);
      }
      if (formData.description) {
        data.append('description', formData.description);
      }
      data.append('file', selectedFile);
      
      await apiClient.post('/admin/presentations/upload', data, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      toast.success('Presentación subida correctamente');
      setShowUploadForm(false);
      setFormData({ title: '', module: '', submodule: '', description: '' });
      setSelectedFile(null);
      fetchPresentations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al subir presentación');
    } finally {
      setUploading(false);
    }
  };

  const [deleteTarget, setDeleteTarget] = useState(null);

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await apiClient.delete(`/admin/presentations/${deleteTarget}`);
      toast.success('Presentación eliminada');
      setDeleteTarget(null);
      fetchPresentations();
    } catch (error) {
      toast.error('Error al eliminar presentación');
    }
  };

  const handleReupload = async (presentationId, file) => {
    if (!file) return;
    setReuploadingId(presentationId);
    try {
      const data = new FormData();
      data.append('file', file);
      await apiClient.post(`/admin/presentations/${presentationId}/reupload`, data, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Archivo re-subido correctamente');
      fetchPresentations();
    } catch (error) {
      toast.error('Error al re-subir archivo');
    }
    setReuploadingId(null);
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="w-8 h-8 border-2 border-teal-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-gray-900">Gestión de Presentaciones</h2>
        <button
          onClick={() => setShowUploadForm(!showUploadForm)}
          className="bg-teal-500 hover:bg-teal-600 text-white font-semibold py-2 px-4 rounded-lg shadow flex items-center"
        >
          <Plus size={18} className="mr-2" />
          Subir Presentación
        </button>
      </div>
      
      {/* Upload Form */}
      {showUploadForm && (
        <div className="bg-gray-50 rounded-xl p-6 mb-6 border border-gray-200">
          <h3 className="text-lg font-semibold mb-4">Nueva Presentación</h3>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Título *
                </label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({...formData, title: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  placeholder="Título de la presentación"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Módulo *
                </label>
                <select
                  value={formData.module}
                  onChange={handleModuleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  required
                >
                  <option value="">Seleccionar módulo</option>
                  {Object.keys(MODULES).map(mod => (
                    <option key={mod} value={mod}>{mod}</option>
                  ))}
                </select>
              </div>
              
              {formData.module && MODULES[formData.module]?.hasSubmodules && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Submódulo *
                  </label>
                  <select
                    value={formData.submodule}
                    onChange={(e) => setFormData({...formData, submodule: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                    required
                  >
                    <option value="">Seleccionar submódulo</option>
                    {MODULES[formData.module].submodules.map(sub => (
                      <option key={sub} value={sub}>{sub}</option>
                    ))}
                  </select>
                </div>
              )}
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Archivo PDF *
                </label>
                <input
                  type="file"
                  accept=".pdf"
                  onChange={handleFileChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  required
                />
                {selectedFile && (
                  <p className="text-sm text-gray-500 mt-1">
                    Archivo: {selectedFile.name} ({(selectedFile.size / 1024 / 1024).toFixed(2)} MB)
                  </p>
                )}
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Descripción (opcional)
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                rows={2}
                placeholder="Descripción breve de la presentación"
              />
            </div>
            
            <div className="flex space-x-3">
              <button
                type="submit"
                disabled={uploading}
                className="bg-teal-500 hover:bg-teal-600 text-white font-semibold py-2 px-6 rounded-lg disabled:opacity-50 flex items-center"
              >
                {uploading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                    Subiendo...
                  </>
                ) : (
                  'Subir Presentación'
                )}
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowUploadForm(false);
                  setFormData({ title: '', module: '', submodule: '', description: '' });
                  setSelectedFile(null);
                }}
                className="bg-gray-300 hover:bg-gray-400 text-gray-700 font-semibold py-2 px-6 rounded-lg"
              >
                Cancelar
              </button>
            </div>
          </form>
        </div>
      )}
      
      {/* Presentations List */}
      <div className="bg-white rounded-xl shadow overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold">
            Presentaciones ({presentations.length})
            {presentations.filter(p => !p.file_available).length > 0 && (
              <span className="ml-2 text-sm font-normal text-red-500">
                ({presentations.filter(p => !p.file_available).length} sin archivo)
              </span>
            )}
          </h3>
        </div>
        
        {presentations.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <p>No hay presentaciones subidas aún.</p>
            <p className="text-sm">Haz clic en "Subir Presentación" para agregar una.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Título
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Módulo
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Submódulo
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Fecha
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Acciones
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {presentations.map((presentation) => (
                  <tr key={presentation.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <span className={`w-2 h-2 rounded-full flex-shrink-0 ${presentation.file_available ? 'bg-green-500' : 'bg-red-500'}`}></span>
                        <div>
                          <div className="font-medium text-gray-900">{presentation.title}</div>
                          {presentation.description && (
                            <div className="text-sm text-gray-500 truncate max-w-xs">
                              {presentation.description}
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {presentation.module}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {presentation.submodule || '-'}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {new Date(presentation.created_at).toLocaleDateString('es-MX')}
                    </td>
                    <td className="px-6 py-4 text-right flex items-center justify-end gap-2">
                      {!presentation.file_available && (
                        <label className="cursor-pointer text-yellow-600 hover:text-yellow-800" title="Re-subir archivo">
                          {reuploadingId === presentation.id ? (
                            <div className="w-4 h-4 border-2 border-yellow-500 border-t-transparent rounded-full animate-spin"></div>
                          ) : (
                            <Upload size={18} />
                          )}
                          <input
                            type="file"
                            accept=".pdf"
                            className="hidden"
                            onChange={(e) => handleReupload(presentation.id, e.target.files[0])}
                          />
                        </label>
                      )}
                      <button
                        onClick={() => setDeleteTarget(presentation.id)}
                        className="text-red-600 hover:text-red-800"
                        title="Eliminar"
                      >
                        <Trash2 size={18} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {deleteTarget && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 max-w-md mx-4 shadow-2xl">
            <h3 className="text-lg font-bold text-gray-900 mb-2">¿Eliminar presentación?</h3>
            <p className="text-gray-600 mb-4">Esta acción no se puede deshacer.</p>
            <div className="flex gap-3">
              <button
                onClick={() => setDeleteTarget(null)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 font-medium"
              >
                Cancelar
              </button>
              <button
                onClick={handleDelete}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 font-semibold"
              >
                Sí, eliminar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ==================== DUEL QUESTIONS ADMIN TAB ====================
const DUEL_SPECIALTIES = [
  'Ginecología y Obstetricia',
  'Cirugía',
  'Pediatría',
  'Medicina Interna',
  'Otros'
];

const DuelQuestionsAdminTab = () => {
  const [stats, setStats] = useState({ stats: [], total: 0 });
  const [questions, setQuestions] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, total_count: 0, total_pages: 0 });
  const [selectedSpecialty, setSelectedSpecialty] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadSpecialty, setUploadSpecialty] = useState('');
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);

  const fetchStats = async () => {
    try {
      const res = await apiClient.get('/admin/duel-questions/stats');
      setStats(res.data);
    } catch (err) {
      console.error('Error fetching stats:', err);
    }
  };

  const fetchQuestions = async (specialty, page = 1) => {
    setLoading(true);
    try {
      const params = { page, page_size: 20 };
      if (specialty) params.specialty = specialty;
      const res = await apiClient.get('/admin/duel-questions', { params });
      setQuestions(res.data.questions);
      setPagination(res.data.pagination);
      setCurrentPage(page);
    } catch (err) {
      console.error('Error fetching questions:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
    fetchQuestions('', 1);
  }, []);

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!uploadSpecialty) {
      toast.error('Selecciona un tema antes de subir');
      return;
    }
    const fileInput = document.getElementById('duel-csv-file');
    const file = fileInput?.files?.[0];
    if (!file) {
      toast.error('Selecciona un archivo CSV');
      return;
    }
    
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('specialty', uploadSpecialty);
      const res = await apiClient.post('/admin/duel-questions/import', formData);
      toast.success(res.data.message);
      fetchStats();
      fetchQuestions(selectedSpecialty, 1);
      fileInput.value = '';
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al importar');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar esta pregunta?')) return;
    try {
      await apiClient.delete(`/admin/duel-questions/${id}`);
      toast.success('Pregunta eliminada');
      fetchStats();
      fetchQuestions(selectedSpecialty, currentPage);
    } catch (err) {
      toast.error('Error al eliminar');
    }
  };

  const handleBulkDelete = async (specialty) => {
    if (!window.confirm(`¿Eliminar TODAS las preguntas de "${specialty}"? Esta acción no se puede deshacer.`)) return;
    try {
      const res = await apiClient.delete(`/admin/duel-questions/bulk/${encodeURIComponent(specialty)}`);
      toast.success(res.data.message);
      fetchStats();
      fetchQuestions(selectedSpecialty, 1);
    } catch (err) {
      toast.error('Error al eliminar');
    }
  };

  const handleFilterChange = (spec) => {
    setSelectedSpecialty(spec);
    fetchQuestions(spec, 1);
  };

  const getStatCount = (specialty) => {
    const s = stats.stats.find(st => st._id === specialty);
    return s ? s.count : 0;
  };

  return (
    <div>
      <h2 className="text-2xl font-bold text-gray-900 mb-6" data-testid="duel-questions-title">Preguntas para Duelos</h2>
      
      {/* Stats Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-8">
        <div className="bg-gray-900 text-white rounded-xl p-4 text-center">
          <div className="text-2xl font-bold">{stats.total}</div>
          <div className="text-xs text-gray-300 mt-1">Total</div>
        </div>
        {DUEL_SPECIALTIES.map(spec => (
          <div key={spec} className="bg-gray-50 border border-gray-200 rounded-xl p-4 text-center">
            <div className="text-2xl font-bold text-gray-900">{getStatCount(spec)}</div>
            <div className="text-xs text-gray-500 mt-1 truncate" title={spec}>{spec}</div>
          </div>
        ))}
      </div>
      
      {/* Upload Section */}
      <div className="bg-gray-50 border border-gray-200 rounded-xl p-6 mb-8">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">Importar Preguntas desde CSV</h3>
        <p className="text-sm text-gray-500 mb-4">
          Formato CSV: <strong>Materia, Tema, frase, opcion A, opcion B, opcion C, letra respuesta correcta</strong>
        </p>
        <form onSubmit={handleUpload} className="flex flex-col sm:flex-row gap-3 items-end">
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-1">Tema base</label>
            <select
              data-testid="upload-specialty-select"
              value={uploadSpecialty}
              onChange={(e) => setUploadSpecialty(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-teal-500"
            >
              <option value="">Seleccionar tema...</option>
              {DUEL_SPECIALTIES.map(s => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </div>
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-1">Archivo CSV</label>
            <input
              id="duel-csv-file"
              type="file"
              accept=".csv"
              data-testid="duel-csv-file-input"
              className="w-full text-sm border border-gray-300 rounded-lg p-2 file:mr-2 file:py-1 file:px-3 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-teal-50 file:text-teal-700"
            />
          </div>
          <button
            type="submit"
            disabled={uploading}
            data-testid="upload-duel-csv-button"
            className="px-6 py-2 bg-teal-600 text-white font-semibold rounded-lg hover:bg-teal-700 transition-colors disabled:bg-gray-400 whitespace-nowrap"
          >
            {uploading ? 'Importando...' : 'Subir CSV'}
          </button>
        </form>
      </div>

      {/* Filter + Bulk Delete */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-4">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm font-medium text-gray-600">Filtrar:</span>
          <button
            onClick={() => handleFilterChange('')}
            className={`px-3 py-1 text-sm rounded-full transition-colors ${
              !selectedSpecialty ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Todos
          </button>
          {DUEL_SPECIALTIES.map(s => (
            <button
              key={s}
              onClick={() => handleFilterChange(s)}
              className={`px-3 py-1 text-sm rounded-full transition-colors ${
                selectedSpecialty === s ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {s} ({getStatCount(s)})
            </button>
          ))}
        </div>
        {selectedSpecialty && getStatCount(selectedSpecialty) > 0 && (
          <button
            onClick={() => handleBulkDelete(selectedSpecialty)}
            className="px-3 py-1 text-sm bg-red-100 text-red-700 rounded-full hover:bg-red-200 transition-colors flex items-center gap-1"
            data-testid="bulk-delete-button"
          >
            <Trash2 size={14} />
            Eliminar todas de {selectedSpecialty}
          </button>
        )}
      </div>

      {/* Questions Table */}
      {loading ? (
        <div className="text-center py-12">
          <div className="animate-spin w-8 h-8 border-2 border-gray-300 border-t-teal-600 rounded-full mx-auto mb-4"></div>
          <p className="text-gray-500">Cargando preguntas...</p>
        </div>
      ) : questions.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          <p className="text-lg font-medium">No hay preguntas cargadas</p>
          <p className="text-sm mt-1">Sube un archivo CSV para empezar</p>
        </div>
      ) : (
        <>
          <div className="overflow-x-auto border border-gray-200 rounded-xl">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tema</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Materia</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase w-1/3">Pregunta</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Respuesta</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Usos</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {questions.map((q) => (
                  <tr key={q.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm text-gray-600">{q.tema}</td>
                    <td className="px-4 py-3 text-sm text-gray-500">{q.materia}</td>
                    <td className="px-4 py-3 text-sm text-gray-900 truncate max-w-xs" title={q.question_text}>{q.question_text}</td>
                    <td className="px-4 py-3 text-sm font-bold text-teal-700">{q.correct_answer}</td>
                    <td className="px-4 py-3 text-sm text-gray-400">{q.global_usage_count}</td>
                    <td className="px-4 py-3 text-right">
                      <button
                        onClick={() => handleDelete(q.id)}
                        className="text-red-500 hover:text-red-700 transition-colors"
                      >
                        <Trash2 size={16} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          {/* Pagination */}
          {pagination.total_pages > 1 && (
            <div className="flex justify-center items-center gap-2 mt-4">
              <button
                onClick={() => fetchQuestions(selectedSpecialty, currentPage - 1)}
                disabled={currentPage <= 1}
                className="px-3 py-1 text-sm border rounded-lg disabled:opacity-30 hover:bg-gray-50"
              >
                Anterior
              </button>
              <span className="text-sm text-gray-600">
                Página {currentPage} de {pagination.total_pages} ({pagination.total_count} preguntas)
              </span>
              <button
                onClick={() => fetchQuestions(selectedSpecialty, currentPage + 1)}
                disabled={currentPage >= pagination.total_pages}
                className="px-3 py-1 text-sm border rounded-lg disabled:opacity-30 hover:bg-gray-50"
              >
                Siguiente
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

// ==================== SIMULACROS ADMIN TAB ====================
const SimulacrosAdminTab = () => {
  const [simulacros, setSimulacros] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [title, setTitle] = useState('');
  const [loading, setLoading] = useState(true);
  const [deleteTarget, setDeleteTarget] = useState(null); // { id, title }
  const [balanceState, setBalanceState] = useState(null);
  const [showBalanceConfirm, setShowBalanceConfirm] = useState(false);
  const [resultsTarget, setResultsTarget] = useState(null); // { id, title }
  const [resultsData, setResultsData] = useState(null);
  const [resultsLoading, setResultsLoading] = useState(false);

  const fetchSimulacros = async () => {
    try {
      const res = await apiClient.get('/admin/simulacros');
      setSimulacros(res.data);
    } catch (err) {
      console.error('Error fetching simulacros:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchBalanceStatus = async () => {
    try {
      const res = await apiClient.get('/admin/simulacros/balance-status');
      setBalanceState(res.data);
      return res.data;
    } catch (err) {
      return null;
    }
  };

  const handleStartBalance = async () => {
    setShowBalanceConfirm(false);
    try {
      const res = await apiClient.post('/admin/simulacros/balance-options');
      toast.success(res.data.message);
      fetchBalanceStatus();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al iniciar el balanceo');
    }
  };

  const openResults = async (sim) => {
    setResultsTarget({ id: sim.id, title: sim.title });
    setResultsData(null);
    setResultsLoading(true);
    try {
      const res = await apiClient.get(`/admin/simulacros/${sim.id}/passers`);
      setResultsData(res.data);
    } catch (err) {
      toast.error('Error al cargar resultados');
      setResultsTarget(null);
    } finally {
      setResultsLoading(false);
    }
  };

  useEffect(() => { fetchSimulacros(); fetchBalanceStatus(); }, []);

  // Poll while balancing is running
  useEffect(() => {
    if (!balanceState?.running) return;
    const interval = setInterval(async () => {
      const st = await fetchBalanceStatus();
      if (st && !st.running) {
        clearInterval(interval);
        fetchSimulacros();
        if (st.status === 'completed') {
          toast.success(`Balanceo completado: ${st.questions_fixed} preguntas ajustadas`);
        } else if (st.status === 'error') {
          toast.error(`Error en balanceo: ${st.error || 'desconocido'}`);
        }
      }
    }, 3000);
    return () => clearInterval(interval);
  }, [balanceState?.running]);

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!title.trim()) {
      toast.error('Ingresa un título para el simulacro');
      return;
    }
    const fileInput = document.getElementById('simulacro-file');
    const file = fileInput?.files?.[0];
    if (!file) {
      toast.error('Selecciona un archivo Excel');
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('title', title.trim());
      const res = await apiClient.post('/admin/simulacros/import', formData);
      toast.success(res.data.message);
      setTitle('');
      fileInput.value = '';
      fetchSimulacros();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al importar');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await apiClient.delete(`/admin/simulacros/${deleteTarget.id}`);
      toast.success('Simulacro eliminado');
      setDeleteTarget(null);
      fetchSimulacros();
    } catch (err) {
      toast.error('Error al eliminar');
    }
  };

  return (
    <div>
      <h2 className="text-2xl font-bold text-gray-900 mb-6" data-testid="simulacros-admin-title">Simulacros</h2>

      {/* Upload Section */}
      <div className="bg-gray-50 border border-gray-200 rounded-xl p-6 mb-8">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">Cargar Nuevo Simulacro</h3>
        <p className="text-sm text-gray-500 mb-4">
          Formato Excel (.xlsx): <strong>numero caso, Caso Clínico, Pregunta, Opción A, Opción B, Opción C, Opción D, Respuesta Correcta, Especialidad, Tema</strong>
        </p>
        <form onSubmit={handleUpload} className="flex flex-col sm:flex-row gap-3 items-end">
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-1">Título del simulacro</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Ej: Simulacro 1"
              data-testid="simulacro-title-input"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-teal-500"
            />
          </div>
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-1">Archivo Excel</label>
            <input
              id="simulacro-file"
              type="file"
              accept=".xlsx,.xls"
              data-testid="simulacro-file-input"
              className="w-full text-sm border border-gray-300 rounded-lg p-2 file:mr-2 file:py-1 file:px-3 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-teal-50 file:text-teal-700"
            />
          </div>
          <button
            type="submit"
            disabled={uploading}
            data-testid="upload-simulacro-button"
            className="px-6 py-2 bg-teal-600 text-white font-semibold rounded-lg hover:bg-teal-700 transition-colors disabled:bg-gray-400 whitespace-nowrap"
          >
            {uploading ? 'Importando...' : 'Subir Simulacro'}
          </button>
        </form>
      </div>

      {/* AI Balance Options Section */}
      <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-6 mb-8" data-testid="balance-options-section">
        <h3 className="text-lg font-semibold text-gray-800 mb-1">Balancear longitud de opciones (IA)</h3>
        <p className="text-sm text-gray-600 mb-4">
          Iguala la longitud de los 4 incisos con IA (Claude) para que la respuesta correcta no sea la más larga y suba la dificultad. Se ejecuta <strong>una sola vez</strong> por examen (los ya balanceados se omiten). Se guarda un respaldo antes de modificar.
        </p>

        {balanceState && (
          <div className="flex flex-wrap items-center gap-4 mb-4 text-sm">
            <span className="px-3 py-1 rounded-full bg-green-100 text-green-800 font-medium" data-testid="balance-count-balanced">
              Balanceados: {balanceState.balanced_simulacros ?? 0}
            </span>
            <span className="px-3 py-1 rounded-full bg-amber-100 text-amber-800 font-medium" data-testid="balance-count-pending">
              Pendientes: {balanceState.pending_simulacros ?? 0}
            </span>
          </div>
        )}

        {balanceState?.running ? (
          <div className="space-y-2" data-testid="balance-progress">
            <div className="flex items-center gap-3">
              <div className="animate-spin w-5 h-5 border-2 border-indigo-300 border-t-indigo-600 rounded-full"></div>
              <span className="text-sm font-medium text-indigo-800">
                Procesando {balanceState.current_simulacro || '...'} — {balanceState.processed_simulacros}/{balanceState.total_simulacros} examen(es)
              </span>
            </div>
            <p className="text-xs text-gray-600">
              Preguntas ajustadas: {balanceState.questions_fixed} (detectadas: {balanceState.questions_to_fix}). Esto puede tardar varios minutos; puedes salir y volver.
            </p>
          </div>
        ) : (
          <button
            onClick={() => setShowBalanceConfirm(true)}
            disabled={balanceState && balanceState.pending_simulacros === 0}
            data-testid="start-balance-button"
            className="px-6 py-2 bg-indigo-600 text-white font-semibold rounded-lg hover:bg-indigo-700 transition-colors disabled:bg-gray-400"
          >
            {balanceState && balanceState.pending_simulacros === 0 ? 'Todos balanceados' : 'Iniciar balanceo con IA'}
          </button>
        )}
      </div>

      {/* List of Simulacros */}
      {loading ? (
        <div className="text-center py-12">
          <div className="animate-spin w-8 h-8 border-2 border-gray-300 border-t-teal-600 rounded-full mx-auto mb-4"></div>
        </div>
      ) : simulacros.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          <p className="text-lg font-medium">No hay simulacros cargados</p>
          <p className="text-sm mt-1">Sube un archivo Excel para crear uno</p>
        </div>
      ) : (
        <div className="space-y-3">
          {simulacros.map((sim) => (
            <div key={sim.id} className="flex items-center justify-between p-4 border border-gray-200 rounded-xl hover:bg-gray-50">
              <div>
                <h4 className="font-semibold text-gray-900 flex items-center gap-2">
                  {sim.title}
                  {sim.options_balanced && (
                    <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-green-100 text-green-700" data-testid={`balanced-badge-${sim.id}`}>
                      Balanceado
                    </span>
                  )}
                </h4>
                <p className="text-sm text-gray-500">
                  {sim.total_questions} preguntas | {sim.total_cases} casos | Creado: {new Date(sim.created_at).toLocaleDateString('es-MX')}
                </p>
              </div>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => openResults(sim)}
                  className="flex items-center gap-1 text-sm text-teal-700 hover:text-teal-900 hover:bg-teal-50 transition-colors px-3 py-2 rounded-lg font-medium"
                  data-testid={`view-results-${sim.id}`}
                >
                  <Users size={16} /> Resultados
                </button>
                <button
                  onClick={() => setDeleteTarget({ id: sim.id, title: sim.title })}
                  className="text-red-500 hover:text-red-700 transition-colors p-2"
                  data-testid={`delete-simulacro-${sim.id}`}
                >
                  <Trash2 size={18} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Simulacro Results (passers) Modal */}
      {resultsTarget && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="simulacro-results-modal">
          <div className="bg-white rounded-xl w-full max-w-2xl max-h-[85vh] flex flex-col shadow-2xl">
            <div className="flex items-center justify-between p-5 border-b border-gray-200">
              <div>
                <h3 className="text-lg font-bold text-gray-900">Aprobados — {resultsTarget.title}</h3>
                <p className="text-sm text-gray-500">Usuarios que terminaron y aprobaron con más de 60%</p>
              </div>
              <button onClick={() => setResultsTarget(null)} className="text-gray-400 hover:text-gray-700 p-1" data-testid="close-results-modal">
                <X size={22} />
              </button>
            </div>
            <div className="overflow-y-auto p-5">
              {resultsLoading ? (
                <div className="text-center py-12">
                  <div className="animate-spin w-8 h-8 border-2 border-gray-300 border-t-teal-600 rounded-full mx-auto"></div>
                </div>
              ) : !resultsData || resultsData.count === 0 ? (
                <div className="text-center py-12 text-gray-500" data-testid="results-empty">
                  <p className="text-base font-medium">Aún no hay usuarios aprobados</p>
                  <p className="text-sm mt-1">Nadie ha terminado este simulacro con más de 60%.</p>
                </div>
              ) : (
                <div>
                  <p className="text-sm text-gray-600 mb-3" data-testid="results-count">
                    <strong>{resultsData.count}</strong> usuario(s) aprobado(s)
                  </p>
                  <div className="space-y-2">
                    {resultsData.passers.map((p, i) => (
                      <div key={p.user_id + i} className="flex items-center justify-between p-3 border border-gray-200 rounded-lg" data-testid={`passer-row-${i}`}>
                        <div className="min-w-0">
                          <p className="font-medium text-gray-900 truncate">{p.full_name}</p>
                          <p className="text-xs text-gray-500 truncate">
                            {p.email}{p.universidad ? ` · ${p.universidad}` : ''}
                            {p.completed_at ? ` · ${new Date(p.completed_at).toLocaleDateString('es-MX')}` : ''}
                          </p>
                        </div>
                        <div className="text-right flex-shrink-0 ml-3">
                          <span className="inline-block px-3 py-1 rounded-full bg-green-100 text-green-800 font-bold text-sm">
                            {p.score_percentage}%
                          </span>
                          <p className="text-[11px] text-gray-400 mt-0.5">{p.correct}/{p.total_questions}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Balance Confirmation Modal */}
      {showBalanceConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" data-testid="balance-confirm-modal">
          <div className="bg-white rounded-xl p-6 max-w-md mx-4 shadow-2xl">
            <h3 className="text-lg font-bold text-gray-900 mb-2">¿Iniciar balanceo con IA?</h3>
            <p className="text-gray-600 mb-4 text-sm">
              Se procesarán <strong>{balanceState?.pending_simulacros ?? 0} simulacro(s)</strong> pendientes con IA (Claude Sonnet 4.6) para igualar la longitud de las opciones. Se creará un respaldo automático antes de modificar. Los ya balanceados se omiten. Esto consume saldo de la Emergent LLM Key.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowBalanceConfirm(false)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 font-medium"
              >
                Cancelar
              </button>
              <button
                onClick={handleStartBalance}
                data-testid="confirm-balance-button"
                className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 font-semibold"
              >
                Sí, iniciar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteTarget && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" data-testid="delete-simulacro-modal">
          <div className="bg-white rounded-xl p-6 max-w-md mx-4 shadow-2xl">
            <h3 className="text-lg font-bold text-gray-900 mb-2">¿Eliminar simulacro?</h3>
            <p className="text-gray-600 mb-4">
              Estás a punto de eliminar <strong>"{deleteTarget.title}"</strong>. Esto también eliminará todos los intentos de los alumnos. Esta acción no se puede deshacer.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setDeleteTarget(null)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 font-medium"
              >
                Cancelar
              </button>
              <button
                onClick={handleDelete}
                data-testid="confirm-delete-simulacro"
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 font-semibold"
              >
                Sí, eliminar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Card Game Admin Tab
const CardGameAdminTab = () => {
  const [decks, setDecks] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [uploading, setUploading] = React.useState(false);
  const [specialty, setSpecialty] = React.useState('Medicina Interna');
  const [subtopic, setSubtopic] = React.useState('');
  const [deckName, setDeckName] = React.useState('');
  const [file, setFile] = React.useState(null);
  const [confirmDeleteId, setConfirmDeleteId] = React.useState(null);

  const specialties = ['Medicina Interna', 'Cirugía', 'Ginecología y Obstetricia', 'Pediatría', 'Otros'];
  const subtopicMap = {
    'Medicina Interna': ['Cardiología', 'Dermatología', 'Endocrinología', 'Gastroenterología', 'Hematología', 'Infectología', 'Medicina Interna (general)', 'Nefrología', 'Neumología', 'Neurología', 'Oncología', 'Reumatología'],
    'Cirugía': ['Angiología', 'Cirugía General', 'Cirugía Maxilofacial', 'Cirugía Plástica', 'Neurocirugía', 'Oftalmología', 'Otorrinolaringología', 'Trasplantes', 'Traumatología y Ortopedia', 'Urología'],
    'Otros': ['Alergia e Inmunología', 'Analgesia y Anestesia', 'Enfermedades Lisosomales', 'Genética', 'Geriatría', 'Medicina Familiar', 'Medicina Física y Rehabilitación', 'Nutriología', 'Psiquiatría', 'Urgencias Médico-Quirúrgicas'],
    'Ginecología y Obstetricia': [],
    'Pediatría': []
  };

  React.useEffect(() => { fetchDecks(); }, []);

  const fetchDecks = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/admin/card-game/decks`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDecks(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async () => {
    if (!file || !deckName.trim()) {
      toast.error('Selecciona un archivo y escribe el nombre del mazo');
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('specialty', specialty);
      formData.append('subtopic', subtopic);
      formData.append('deck_name', deckName.trim());
      formData.append('file', file);
      const token = localStorage.getItem('token');
      const res = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/admin/card-game/upload`, formData, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
      });
      toast.success(res.data.message);
      setFile(null);
      setDeckName('');
      fetchDecks();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al subir');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (deckId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${process.env.REACT_APP_BACKEND_URL}/api/admin/card-game/decks/${deckId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Mazo eliminado');
      setConfirmDeleteId(null);
      fetchDecks();
    } catch (err) {
      toast.error('Error al eliminar');
    }
  };

  const currentSubtopics = subtopicMap[specialty] || [];

  return (
    <div data-testid="card-game-admin-tab">
      <h2 className="text-xl font-bold mb-4">Juego de Cartas - Administrar Mazos</h2>

      {/* Upload form */}
      <div className="bg-gray-50 rounded-lg p-4 mb-6 border">
        <h3 className="font-bold mb-3">Subir nuevo mazo</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Especialidad</label>
            <select value={specialty} onChange={(e) => { setSpecialty(e.target.value); setSubtopic(''); }} className="w-full border rounded-lg px-3 py-2 text-sm" data-testid="card-game-specialty-select">
              {specialties.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Subtema</label>
            <select value={subtopic} onChange={(e) => setSubtopic(e.target.value)} className="w-full border rounded-lg px-3 py-2 text-sm" data-testid="card-game-subtopic-select" disabled={currentSubtopics.length === 0}>
              <option value="">-- Sin subtema --</option>
              {currentSubtopics.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Nombre del mazo</label>
            <input type="text" value={deckName} onChange={(e) => setDeckName(e.target.value)} placeholder="Ej: Síndrome Nefrótico" className="w-full border rounded-lg px-3 py-2 text-sm" data-testid="card-game-deck-name" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Archivo Excel</label>
            <input type="file" accept=".xlsx,.xls" onChange={(e) => setFile(e.target.files[0])} className="w-full border rounded-lg px-3 py-2 text-sm" data-testid="card-game-file-input" />
          </div>
        </div>
        <p className="text-xs text-gray-500 mb-3">Columnas requeridas: Pregunta, Opcion A, Opcion B, Respuesta Correcta (A o B), Justificacion y Significado. El archivo debe tener 45 preguntas: filas 1-15 = Leves, 16-30 = Moderadas, 31-40 = Dificiles, 41-45 = Muy dificiles. Se seleccionaran 15 preguntas aleatorias proporcionales por dificultad.</p>
        <button onClick={handleUpload} disabled={uploading} className="px-4 py-2 bg-black text-white rounded-lg text-sm font-medium hover:bg-gray-800 disabled:opacity-50" data-testid="card-game-upload-btn">
          {uploading ? 'Subiendo...' : 'Subir Mazo'}
        </button>
      </div>

      {/* Deck list */}
      <h3 className="font-bold mb-3">Mazos existentes ({decks.length})</h3>
      {loading ? (
        <p className="text-gray-500">Cargando...</p>
      ) : decks.length === 0 ? (
        <p className="text-gray-500">No hay mazos. Sube el primer mazo.</p>
      ) : (
        <div className="space-y-2">
          {decks.map(deck => (
            <div key={deck.id} className="flex items-center justify-between bg-white border rounded-lg p-3" data-testid={`admin-deck-${deck.id}`}>
              <div>
                <span className="font-medium">{deck.name}</span>
                <span className="text-xs text-gray-500 ml-2">{deck.specialty}{deck.subtopic ? ` / ${deck.subtopic}` : ''}</span>
                <span className="text-xs text-gray-400 ml-2">{deck.total_cards} cartas</span>
              </div>
              <div className="flex items-center gap-2">
                {confirmDeleteId === deck.id ? (
                  <>
                    <span className="text-sm text-gray-500">Seguro?</span>
                    <button onClick={() => handleDelete(deck.id)} className="text-red-600 hover:text-red-800 text-sm font-bold" data-testid={`confirm-delete-deck-${deck.id}`}>Si</button>
                    <button onClick={() => setConfirmDeleteId(null)} className="text-gray-500 hover:text-gray-700 text-sm font-medium" data-testid={`cancel-delete-deck-${deck.id}`}>No</button>
                  </>
                ) : (
                  <button onClick={() => setConfirmDeleteId(deck.id)} className="text-red-600 hover:text-red-800 text-sm font-medium" data-testid={`delete-deck-${deck.id}`}>Eliminar</button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};



// Admin Points Panel sub-component
const AdminPointsPanel = ({ apiClient, user, onUserUpdate }) => {
  const [points, setPoints] = React.useState('');
  const [currentData, setCurrentData] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [gender, setGender] = React.useState('male');

  const RANKS = [
    { key: "estudiante_de_medicina", name: "Estudiante de Medicina", points: 0 },
    { key: "interno_de_pregrado", name: "Interno de Pregrado", points: 200 },
    { key: "medico_pasante", name: "Médico Pasante de Servicio Social", points: 600 },
    { key: "medico_general", name: "Médico General", points: 1200 },
    { key: "residente_primer_ano", name: "Residente de Primer Año", points: 2000 },
    { key: "residente_ultimo_ano", name: "Residente de Último Año", points: 3200 },
    { key: "jefe_de_residentes", name: "Jefe de Residentes", points: 5000 },
    { key: "medico_especialista", name: "Médico Especialista", points: 8000 },
    { key: "subespecialista", name: "Subespecialista", points: 14000 },
    { key: "alta_especialidad", name: "Alta Especialidad", points: 30000 },
    { key: "maestria_ciencias", name: "Maestría en Ciencias Médicas", points: 50000 },
    { key: "doctorado_ciencias", name: "Doctorado en Ciencias Médicas", points: 80000 },
    { key: "jefe_de_servicio", name: "Jefe de Servicio", points: 120000 },
    { key: "director_hospital", name: "Director de Hospital", points: 180000 },
    { key: "secretario_salud", name: "Secretario de Salud", points: 260000 },
    { key: "director_oms", name: "Director General de la OMS", points: 400000 },
    { key: "premio_nobel", name: "Premio Nobel de Medicina", points: 700000 },
  ];

  const fetchCurrent = React.useCallback(async () => {
    try {
      const res = await apiClient.get('/points/me');
      setCurrentData(res.data);
      setPoints(String(res.data.total_points));
      // Also fetch current gender
      const meRes = await apiClient.get('/auth/me');
      if (meRes.data.gender) setGender(meRes.data.gender);
    } catch (e) { console.error(e); }
  }, [apiClient]);

  React.useEffect(() => { fetchCurrent(); }, [fetchCurrent]);

  const handleSet = async () => {
    setLoading(true);
    try {
      const res = await apiClient.post('/admin/set-points', { points: parseInt(points) || 0 });
      setCurrentData({ ...currentData, total_points: res.data.total_points, current_rank: res.data.current_rank, next_rank: res.data.next_rank });
      toast.success(`Puntos establecidos: ${res.data.total_points.toLocaleString()}`);
    } catch (e) {
      toast.error('Error al establecer puntos');
    }
    setLoading(false);
  };

  const handleGenderChange = async (g) => {
    setGender(g);
    try {
      await apiClient.post('/auth/update-gender', { gender: g });
      if (onUserUpdate && user) onUserUpdate({ ...user, gender: g });
      toast.success(`Género cambiado a ${g === 'male' ? 'Hombre' : 'Mujer'}`);
    } catch (e) {
      toast.error('Error al cambiar género');
    }
  };

  const currentRankKey = currentData?.current_rank?.key || 'estudiante_de_medicina';

  return (
    <div>
      <h2 className="text-2xl font-bold text-gray-900 mb-4">Control de Puntos (Admin)</h2>
      <p className="text-gray-500 text-sm mb-6">Establece manualmente el puntaje del administrador para probar los rangos y las imágenes de evolución.</p>

      <div className="bg-white border rounded-lg p-6 mb-6">
        {/* Gender toggle */}
        <div className="mb-4">
          <label className="block text-sm font-semibold text-gray-700 mb-1">Género del personaje</label>
          <div className="flex gap-2">
            <button
              onClick={() => handleGenderChange('male')}
              data-testid="admin-gender-male"
              className={`px-4 py-2 rounded-lg font-semibold text-sm transition-all ${
                gender === 'male' ? 'bg-black text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >Hombre</button>
            <button
              onClick={() => handleGenderChange('female')}
              data-testid="admin-gender-female"
              className={`px-4 py-2 rounded-lg font-semibold text-sm transition-all ${
                gender === 'female' ? 'bg-black text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >Mujer</button>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-4 items-end">
          <div className="flex-1">
            <label className="block text-sm font-semibold text-gray-700 mb-1">Puntos del Admin</label>
            <input
              type="number"
              value={points}
              onChange={(e) => setPoints(e.target.value)}
              data-testid="admin-points-input"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-black text-lg font-bold"
              placeholder="Ej: 200"
              min="0"
            />
          </div>
          <button
            onClick={handleSet}
            disabled={loading}
            data-testid="admin-set-points-btn"
            className="px-6 py-3 bg-black text-white rounded-lg font-semibold hover:bg-gray-800 disabled:opacity-50"
          >
            {loading ? 'Guardando...' : 'Establecer Puntos'}
          </button>
        </div>

        {currentData && (
          <div className="mt-4 p-4 bg-gray-50 rounded-lg flex items-center gap-4">
            <img
              src={`/sprites/${currentRankKey}_${gender}.png?v=3`}
              alt={currentData.current_rank?.name}
              className="w-20 h-24 object-contain"
            />
            <div>
              <p className="text-sm"><strong>Puntos actuales:</strong> {currentData.total_points?.toLocaleString()}</p>
              <p className="text-sm"><strong>Rango actual:</strong> {currentData.current_rank?.name}</p>
              {currentData.next_rank && (
                <p className="text-sm"><strong>Siguiente rango:</strong> {currentData.next_rank?.name} ({currentData.next_rank?.points?.toLocaleString()} pts)</p>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Quick rank buttons */}
      <h3 className="text-lg font-bold text-gray-800 mb-3">Saltar a rango</h3>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
        {RANKS.map((rank) => (
          <button
            key={rank.key}
            onClick={() => { setPoints(String(rank.points)); }}
            data-testid={`rank-btn-${rank.key}`}
            className={`text-left px-3 py-2 rounded-lg border text-sm font-medium transition-all ${
              currentData?.current_rank?.key === rank.key
                ? 'border-black bg-black text-white'
                : 'border-gray-200 hover:border-gray-400 text-gray-700'
            }`}
          >
            <span className="block font-bold">{rank.name}</span>
            <span className="block text-xs opacity-70">{rank.points.toLocaleString()} pts</span>
          </button>
        ))}
      </div>
    </div>
  );
};

// Journal Panel sub-component
const JournalPanel = ({ apiClient }) => {
  const [status, setStatus] = React.useState(null);
  const [uploading, setUploading] = React.useState(false);
  const [rotating, setRotating] = React.useState(false);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    fetchStatus();
  }, []);

  const fetchStatus = async () => {
    try {
      setLoading(true);
      const res = await apiClient.get('/admin/journal/status');
      setStatus(res.data);
    } catch (error) {
      console.error('Error fetching journal status:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await apiClient.post('/admin/journal/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success(res.data.message);
      fetchStatus();
    } catch (error) {
      toast.error('Error al subir el archivo');
      console.error(error);
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleRotate = async () => {
    setRotating(true);
    try {
      const res = await apiClient.post('/admin/journal/rotate');
      toast.success(res.data.message);
      fetchStatus();
    } catch (error) {
      toast.error('Error al rotar artículo');
      console.error(error);
    } finally {
      setRotating(false);
    }
  };

  if (loading) {
    return <div className="text-center py-8 text-gray-500">Cargando...</div>;
  }

  return (
    <div>
      <h2 className="text-2xl font-bold text-gray-900 mb-6">Journal - Gestión de Artículos</h2>

      {/* Upload Section */}
      <div className="bg-white border border-gray-200 rounded-lg p-6 mb-6">
        <h3 className="text-lg font-semibold mb-3">Subir Archivo Excel</h3>
        <p className="text-sm text-gray-600 mb-4">
          Sube un archivo Excel con columnas: Tema, Antecedentes, Metodos, Resultados, Conclusiones.
          El sistema seleccionará un artículo aleatorio cada día a las 9 PM (CDMX) sin repetir.
        </p>
        <label
          data-testid="journal-upload-label"
          className={`inline-flex items-center px-6 py-3 border border-black rounded-lg cursor-pointer transition-colors ${
            uploading ? 'bg-gray-200 text-gray-500' : 'bg-black text-white hover:bg-gray-800'
          }`}
        >
          {uploading ? 'Subiendo...' : 'Seleccionar Excel'}
          <input
            type="file"
            accept=".xlsx,.xls"
            onChange={handleUpload}
            disabled={uploading}
            className="hidden"
            data-testid="journal-upload-input"
          />
        </label>
      </div>

      {/* Status Section */}
      <div className="bg-white border border-gray-200 rounded-lg p-6 mb-6">
        <h3 className="text-lg font-semibold mb-4">Estado del Sistema</h3>
        <div className="grid grid-cols-3 gap-4 mb-4">
          <div className="text-center p-4 bg-gray-50 rounded-lg">
            <div className="text-2xl font-bold text-black">{status?.total_articles || 0}</div>
            <div className="text-xs text-gray-500">Total artículos</div>
          </div>
          <div className="text-center p-4 bg-gray-50 rounded-lg">
            <div className="text-2xl font-bold text-black">{status?.used_articles || 0}</div>
            <div className="text-xs text-gray-500">Utilizados</div>
          </div>
          <div className="text-center p-4 bg-gray-50 rounded-lg">
            <div className="text-2xl font-bold text-black">{status?.remaining_articles || 0}</div>
            <div className="text-xs text-gray-500">Restantes</div>
          </div>
        </div>

        <button
          onClick={handleRotate}
          disabled={rotating || (status?.total_articles || 0) === 0}
          data-testid="journal-rotate-button"
          className={`px-6 py-2 rounded-lg border transition-colors ${
            rotating || (status?.total_articles || 0) === 0
              ? 'bg-gray-200 text-gray-400 border-gray-200'
              : 'bg-white text-black border-black hover:bg-gray-100'
          }`}
        >
          {rotating ? 'Rotando...' : 'Rotar Artículo Manualmente'}
        </button>
      </div>

      {/* Current Article Preview */}
      {status?.current_article && (
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold mb-4">Artículo Actual</h3>
          <div className="space-y-3">
            <div>
              <span className="text-xs font-medium text-gray-500 uppercase">Issue</span>
              <p className="text-sm">{status.current_article.issue_number} — {status.current_article.date_str}</p>
            </div>
            <div>
              <span className="text-xs font-medium text-gray-500 uppercase">Tema</span>
              <p className="text-sm font-semibold">{status.current_article.tema}</p>
            </div>
            <div>
              <span className="text-xs font-medium text-gray-500 uppercase">Antecedentes</span>
              <p className="text-xs text-gray-700">{status.current_article.antecedentes?.substring(0, 200)}...</p>
            </div>
            <div>
              <span className="text-xs font-medium text-gray-500 uppercase">Métodos</span>
              <p className="text-xs text-gray-700">{status.current_article.metodos?.substring(0, 200)}...</p>
            </div>
            <div>
              <span className="text-xs font-medium text-gray-500 uppercase">Resultados</span>
              <p className="text-xs text-gray-700">{status.current_article.resultados?.substring(0, 200)}...</p>
            </div>
            <div>
              <span className="text-xs font-medium text-gray-500 uppercase">Conclusiones</span>
              <p className="text-xs text-gray-700">{status.current_article.conclusiones?.substring(0, 200)}...</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Support Admin Panel Component
const SupportAdminPanel = ({ apiClient, tickets, expandedTicket, setExpandedTicket, replyText, setReplyText, onRefresh }) => {
  const formatDate = (dateStr) => {
    const d = new Date(dateStr);
    return d.toLocaleDateString('es-MX', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const handleReply = async (ticketId) => {
    if (!replyText.trim()) return;
    try {
      await apiClient.post(`/support/reply/${ticketId}`, { message: replyText.trim() });
      toast.success('Respuesta enviada');
      setReplyText('');
      // Mark as read
      await apiClient.put(`/support/mark-read/${ticketId}`);
      onRefresh();
    } catch (err) {
      toast.error('Error al enviar respuesta');
    }
  };

  const handleMarkRead = async (ticketId) => {
    try {
      await apiClient.put(`/support/mark-read/${ticketId}`);
      onRefresh();
    } catch (err) { /* ignore */ }
  };

  const getCategoryLabel = (cat) => {
    switch (cat) {
      case 'error': return 'Error';
      case 'sugerencia': return 'Sugerencia';
      case 'contenido': return 'Contenido';
      default: return 'General';
    }
  };

  const getCategoryColor = (cat) => {
    switch (cat) {
      case 'error': return 'bg-red-100 text-red-800';
      case 'sugerencia': return 'bg-blue-100 text-blue-800';
      case 'contenido': return 'bg-purple-100 text-purple-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div data-testid="support-admin-panel">
      <h2 className="text-2xl font-bold text-gray-900 mb-4">Sugerencias y Soporte</h2>
      <p className="text-gray-500 text-sm mb-6">Solicitudes de los usuarios ({tickets.length})</p>

      {tickets.length === 0 ? (
        <div className="text-center py-12 text-gray-400" data-testid="support-admin-empty">
          No hay solicitudes
        </div>
      ) : (
        <div className="space-y-3">
          {tickets.map((ticket) => (
            <div
              key={ticket.ticket_id}
              className={`bg-white rounded-xl border overflow-hidden ${!ticket.admin_read ? 'border-yellow-400 border-2' : 'border-gray-200'}`}
              data-testid={`admin-ticket-${ticket.ticket_id}`}
            >
              <button
                onClick={() => {
                  setExpandedTicket(expandedTicket === ticket.ticket_id ? null : ticket.ticket_id);
                  if (!ticket.admin_read) handleMarkRead(ticket.ticket_id);
                }}
                className="w-full px-4 py-3 flex items-start justify-between text-left hover:bg-gray-50 transition-colors"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    {!ticket.admin_read && (
                      <span className="inline-flex items-center justify-center w-2.5 h-2.5 bg-yellow-400 rounded-full" />
                    )}
                    <span className="text-sm font-semibold text-gray-900">{ticket.user_name}</span>
                    <span className="text-xs text-gray-400">({ticket.user_email})</span>
                    <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${getCategoryColor(ticket.category)}`}>
                      {getCategoryLabel(ticket.category)}
                    </span>
                    <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${
                      ticket.status === 'open' ? 'bg-yellow-100 text-yellow-800' : 'bg-green-100 text-green-800'
                    }`}>
                      {ticket.status === 'open' ? 'Pendiente' : 'Respondido'}
                    </span>
                  </div>
                  <p className="text-sm text-gray-700 line-clamp-2">{ticket.message}</p>
                  <p className="text-xs text-gray-400 mt-1">{formatDate(ticket.created_at)}</p>
                </div>
              </button>

              {expandedTicket === ticket.ticket_id && (
                <div className="border-t border-gray-100 px-4 py-3 bg-gray-50">
                  {/* Original message */}
                  <div className="p-3 bg-white border border-gray-200 rounded-lg mb-3 mr-8">
                    <p className="text-xs text-gray-500 font-medium mb-1">{ticket.user_name}</p>
                    <p className="text-sm whitespace-pre-wrap">{ticket.message}</p>
                  </div>

                  {/* Replies */}
                  {ticket.replies && ticket.replies.length > 0 && (
                    <div className="space-y-2 mb-3">
                      {ticket.replies.map((reply, idx) => (
                        <div
                          key={reply.reply_id || idx}
                          className={`p-3 rounded-lg text-sm ${
                            reply.from_role === 'admin'
                              ? 'bg-black text-white ml-8'
                              : 'bg-white border border-gray-200 mr-8'
                          }`}
                        >
                          <p className={`text-xs font-medium mb-1 ${reply.from_role === 'admin' ? 'text-gray-300' : 'text-gray-500'}`}>
                            {reply.from_role === 'admin' ? 'Admin' : ticket.user_name}
                          </p>
                          <p className="whitespace-pre-wrap">{reply.message}</p>
                          <p className={`text-xs mt-1 ${reply.from_role === 'admin' ? 'text-gray-400' : 'text-gray-400'}`}>
                            {formatDate(reply.created_at)}
                          </p>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Admin reply */}
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={replyText}
                      onChange={(e) => setReplyText(e.target.value)}
                      placeholder="Escribe tu respuesta al usuario..."
                      className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-black"
                      onKeyDown={(e) => e.key === 'Enter' && handleReply(ticket.ticket_id)}
                      data-testid="admin-reply-input"
                    />
                    <button
                      onClick={() => handleReply(ticket.ticket_id)}
                      disabled={!replyText.trim()}
                      className="px-4 py-2 bg-black text-white rounded-lg text-sm font-medium hover:bg-gray-800 disabled:opacity-40"
                      data-testid="admin-reply-send-btn"
                    >
                      Enviar
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Admin Account Change Panel
const AdminAccountPanel = ({ apiClient }) => {
  const [newName, setNewName] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [sending, setSending] = useState(false);
  const [message, setMessage] = useState(null);

  const handleChangeName = async () => {
    if (!newName.trim() || newName.trim().length < 3) {
      toast.error('El nombre debe tener al menos 3 caracteres');
      return;
    }
    setSending(true);
    setMessage(null);
    try {
      const res = await apiClient.post('/admin/request-account-change', {
        change_type: 'name',
        new_value: newName.trim()
      });
      setMessage({ type: 'success', text: res.data.message });
      setNewName('');
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.detail || 'Error al enviar solicitud' });
    } finally {
      setSending(false);
    }
  };

  const handleChangePassword = async () => {
    if (!newPassword || newPassword.length < 6) {
      toast.error('La contrasena debe tener al menos 6 caracteres');
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error('Las contrasenas no coinciden');
      return;
    }
    setSending(true);
    setMessage(null);
    try {
      const res = await apiClient.post('/admin/request-account-change', {
        change_type: 'password',
        new_value: newPassword
      });
      setMessage({ type: 'success', text: res.data.message });
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.detail || 'Error al enviar solicitud' });
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="admin-account-panel">
      <h2 className="text-2xl font-bold text-gray-900 mb-2">Mi Cuenta</h2>
      <p className="text-gray-500 text-sm mb-6">Cambia el nombre o contrasena de la cuenta de administrador. Se enviara un enlace de confirmacion a tu correo.</p>

      {message && (
        <div className={`mb-6 p-4 rounded-lg text-sm ${message.type === 'success' ? 'bg-green-50 text-green-800 border border-green-200' : 'bg-red-50 text-red-800 border border-red-200'}`} data-testid="account-change-message">
          {message.text}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Change Name */}
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <h3 className="text-base font-semibold text-gray-900 mb-4">Cambiar Nombre</h3>
          <div className="space-y-3">
            <div>
              <label className="block text-sm text-gray-600 mb-1">Nuevo nombre</label>
              <input
                type="text"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder="Nuevo nombre del administrador"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-black"
                data-testid="admin-new-name-input"
              />
            </div>
            <button
              onClick={handleChangeName}
              disabled={sending || !newName.trim()}
              className="w-full px-4 py-2.5 bg-black text-white text-sm font-medium rounded-lg hover:bg-gray-800 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              data-testid="admin-change-name-btn"
            >
              {sending ? 'Enviando...' : 'Enviar enlace de confirmacion'}
            </button>
          </div>
        </div>

        {/* Change Password */}
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <h3 className="text-base font-semibold text-gray-900 mb-4">Cambiar Contrasena</h3>
          <div className="space-y-3">
            <div>
              <label className="block text-sm text-gray-600 mb-1">Nueva contrasena</label>
              <input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Min. 6 caracteres"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-black"
                data-testid="admin-new-password-input"
              />
            </div>
            <div>
              <label className="block text-sm text-gray-600 mb-1">Confirmar contrasena</label>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Repite la contrasena"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-black"
                data-testid="admin-confirm-password-input"
              />
            </div>
            <button
              onClick={handleChangePassword}
              disabled={sending || !newPassword || !confirmPassword}
              className="w-full px-4 py-2.5 bg-black text-white text-sm font-medium rounded-lg hover:bg-gray-800 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              data-testid="admin-change-password-btn"
            >
              {sending ? 'Enviando...' : 'Enviar enlace de confirmacion'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const AdminSettingsPanel = ({ apiClient }) => {
  const [price, setPrice] = React.useState('');
  const [currentPrice, setCurrentPrice] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [saving, setSaving] = React.useState(false);

  React.useEffect(() => {
    setLoading(true);
    apiClient.get('/admin/settings/price')
      .then(res => {
        setCurrentPrice(res.data.price);
        setPrice(String(res.data.price));
      })
      .catch(() => setCurrentPrice(1500))
      .finally(() => setLoading(false));
  }, [apiClient]);

  const handleSavePrice = async () => {
    const numPrice = parseFloat(price);
    if (isNaN(numPrice) || numPrice <= 0) {
      toast.error('Ingresa un precio valido mayor a 0');
      return;
    }
    setSaving(true);
    try {
      await apiClient.post('/admin/settings/price', { price: numPrice });
      setCurrentPrice(numPrice);
      toast.success('Precio actualizado correctamente');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al actualizar precio');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-settings-panel">
      <h2 className="text-2xl font-bold text-gray-900">Configuracion de la App</h2>

      <div className="bg-white border border-gray-200 rounded-xl p-6 max-w-md">
        <h3 className="text-lg font-semibold text-gray-900 mb-1">Precio de Suscripcion</h3>
        <p className="text-sm text-gray-500 mb-4">Este precio se muestra en el landing page y se cobra via MercadoPago (6 meses de acceso).</p>

        {loading ? (
          <div className="text-gray-400 text-sm">Cargando...</div>
        ) : (
          <>
            {currentPrice !== null && (
              <p className="text-sm text-gray-500 mb-3">
                Precio actual: <span className="font-bold text-black">${Number(currentPrice).toLocaleString('es-MX')} MXN</span>
              </p>
            )}
            <div className="flex gap-3 items-end">
              <div className="flex-1">
                <label className="block text-xs font-medium text-gray-500 mb-1">Nuevo precio (MXN)</label>
                <input
                  type="number"
                  min="1"
                  step="1"
                  value={price}
                  onChange={e => setPrice(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-black"
                  data-testid="settings-price-input"
                />
              </div>
              <button
                onClick={handleSavePrice}
                disabled={saving}
                className="px-5 py-2.5 bg-black text-white text-sm font-semibold rounded-lg hover:bg-gray-800 disabled:opacity-40 transition-colors"
                data-testid="settings-price-save-btn"
              >
                {saving ? 'Guardando...' : 'Guardar'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AdminPanel;